import { z } from 'zod';

/**
 * The closed vocabularies of the stage-1 contract, in one place: run states,
 * decision kinds, correction semantics, prose kinds, source tiers, sensitivity,
 * the six facet keys, the sixteen fact types, and the proposed fact-type →
 * brief-section mapping.
 *
 * Sources: server-design.md §5.1 (run states), §3.4/§7.6 + agent-architecture.md
 * §3.3 (decision kinds), §6.2 (correction semantics), §8 (source tiers, prose
 * kinds, statuses), §7.1 (sensitivity, severity), §5.2 (facets),
 * fact-catalog.md (fact types).
 */

/* ————————————————————————— runs ————————————————————————— */

/**
 * `prose_refresh` joined on 2026-09-01 (docs/prose-rerun-design.md §3): a run
 * that enters directly at the summary stage and rewrites the brief from the
 * record the subject already holds — no retrieval, no extraction, no bindings.
 * It is a third **kind** rather than a flag on `research` because every
 * kind-gated behaviour in this codebase (creation preconditions, continuation
 * dispatch, strip copy, history labels) discriminates on `kind`, and a hidden
 * sub-kind would need a second discriminator at each of them.
 *
 * Widening this list is what makes a prose-rerun row parse at all: `RunSummary`
 * and `RunDetail` type `kind` as `z.enum(RUN_KINDS)` and are re-parsed
 * server-side on every read, so an un-widened vocabulary 500s the run reads and
 * the People list rather than merely mislabelling a row.
 */
/**
 * `org_lookup` joined on 2026-09-09 (organisations-as-subjects note §1 R3/R3a,
 * §2 chunk 3): the one run an **organisation** can be the subject of.
 *
 * A fourth kind rather than a depth of `research` for the same reason
 * `prose_refresh` is a third: every kind-gated behaviour here discriminates on
 * `kind`, and the two runs share almost nothing below it. A research run is
 * downstream of an identity lock, plans against the six person facets, and
 * derives its analysis kinds from a status; an organisation has no anchor set to
 * lock, no status to derive from, and its facets are not in `FACET_KEYS` at all.
 *
 * **The depth rides `config.depth`, not the kind** (§2 chunk 3 "As grounded" 2),
 * which bends the house rule that a button names the kind. For a person the
 * depth comes from status; an organisation has none, so the office chooses, and
 * the record offers "Look this up" and "Investigate" over one kind.
 */
/**
 * **A report's four runs** (reports design §4.3). They target a report rather
 * than a subject (`run.report_id`), never touch the record, and start only from
 * the report routes, never from `POST /subjects/:id/runs`.
 */
export const REPORT_RUN_KINDS = [
  'report_generate',
  'report_update',
  'report_refresh',
  'report_revise',
] as const;
export const ReportRunKind = z.enum(REPORT_RUN_KINDS);
export type ReportRunKind = z.infer<typeof ReportRunKind>;

export function isReportRunKind(kind: string): kind is ReportRunKind {
  return (REPORT_RUN_KINDS as readonly string[]).includes(kind);
}

/* The report kinds are spelled out rather than spread: the agent's pin test
   (`tests/research/graphs/test_org_lookup.py`) reads this list as literals. */
export const RUN_KINDS = [
  'quick_search',
  'research',
  'prose_refresh',
  'org_lookup',
  'event_research',
  'event_scan',
  'report_generate',
  'report_update',
  'report_refresh',
  'report_revise',
] as const;

// Every report kind is a run kind; a compile error if the two lists drift.
const _reportKindsAreRunKinds: readonly (typeof RUN_KINDS)[number][] = REPORT_RUN_KINDS;
void _reportKindsAreRunKinds;
export const RunKind = z.enum(RUN_KINDS);
export type RunKind = z.infer<typeof RunKind>;

/**
 * **Which pass a person's research run was** (dossier Re-run and Refresh spec
 * §3.1). All three are `research`; the kind cannot tell them apart.
 *
 * - `dossier`: the subject's first research run, or any run before one completed.
 * - `rerun`: a from-scratch run after a dossier completed.
 * - `refresh`: every initial leg an `update` since the last dossier.
 */
export const RESEARCH_PASSES = ['dossier', 'rerun', 'refresh'] as const;
export const ResearchPass = z.enum(RESEARCH_PASSES);
export type ResearchPass = z.infer<typeof ResearchPass>;

/**
 * `checkpointed` sits between `running` and the `awaiting_*` family on purpose:
 * it is a **deliberate between-legs stop**, not a wait on a person. The pipeline
 * hit its soft deadline, banked what it had, and named the successor leg the
 * server enqueues on its way out — so nothing is owed by the office, and the run
 * is still live. It is the one non-`awaiting_*` stop a run can make and come
 * back from.
 */
export const RUN_STATES = [
  'created',
  'running',
  'checkpointed',
  'awaiting_identity_pick',
  'awaiting_prose',
  'awaiting_adjudication',
  'complete',
  'failed',
] as const;
export const RunState = z.enum(RUN_STATES);
export type RunState = z.infer<typeof RunState>;

/** The states a run sits in while it waits on a human. */
export const AWAITING_RUN_STATES = [
  'awaiting_identity_pick',
  'awaiting_prose',
  'awaiting_adjudication',
] as const;
export const AwaitingRunState = z.enum(AWAITING_RUN_STATES);
export type AwaitingRunState = z.infer<typeof AwaitingRunState>;

/** Terminal states — no continuation is owed. */
export const TERMINAL_RUN_STATES = ['complete', 'failed'] as const;

/**
 * **What a run is called, in the office's words.**
 *
 * **In `@ni/shared` since 2026-09-10**, lifted out of `dashboard/src/lib/copy.ts`
 * for the reason {@link SOURCE_TIER_LABELS} was: the MCP connector is a second
 * reader, and it hands `latest_run.kind` to a model that repeats whatever string
 * it is given. `prose_refresh` read aloud is the same defect as `osint_run` was.
 * The dashboard re-exports these and no call site there changed.
 */
export const RUN_KIND_LABELS: Record<RunKind, string> = {
  quick_search: 'Quick search',
  /* "Research", not "Full dossier" (2026-09-05). The label rides the run strip
     and every history row, and since the club's other three words went live a
     research run is only *sometimes* a full dossier: a member's and a contact's
     is a light pass over three facets. A label that names the kind is true of
     all of them; one that names the depth was about to be false half the time. */
  research: 'Research',
  // The run-history row is the audit trail for "who rewrote the brief, when",
  // so the label names the act rather than the machinery (§6.4).
  prose_refresh: 'Brief rewrite',
  /* One label over both depths, because the label names the *kind* and the
     depth rides `config.depth` (organisations-as-subjects note §2 chunk 3 "As
     grounded" 2) — the same ruling that made `research` stop reading "Full
     dossier". The record's two buttons say "Look this up" and "Investigate";
     the strip and the history row say what kind of run it was. 3c is where the
     strip narration and the buttons land. */
  org_lookup: 'Lookup',
  event_research: 'Event research',
  event_scan: 'Attendee scan',
  report_generate: 'Report',
  report_update: 'Report update',
  report_refresh: 'Report refresh',
  report_revise: 'Report revision',
};

/** A research run's label by its pass; `dossier` keeps the kind's own word. */
export const RESEARCH_PASS_LABELS: Record<ResearchPass, string> = {
  dossier: RUN_KIND_LABELS.research,
  rerun: 'Research again',
  refresh: 'Refresh',
};

/** What a run is called, telling a research run's passes apart. */
export function runHistoryLabel(run: {
  kind: RunKind;
  research_pass: ResearchPass | null;
}): string {
  return run.research_pass ? RESEARCH_PASS_LABELS[run.research_pass] : RUN_KIND_LABELS[run.kind];
}

/**
 * Terminal-and-quiet labels for run history; the live strip has its own copy.
 *
 * `awaiting_adjudication` reads "Waiting on review" here and stays that way
 * after §3f widened what the run waits on: the label names the state, not the
 * count, and a state that now covers queued questions as well as blocking ones
 * is still exactly a run waiting on review. The counted, present-tense version
 * ("Paused · 3 findings need your review before the summary") belongs to the run
 * strip, which is the surface the office is looking at while it is true.
 *
 * Shared since 2026-09-10 alongside {@link RUN_KIND_LABELS}, and for the same
 * reason: `awaiting_adjudication` is the token a model was reading out.
 */
export const RUN_STATE_LABELS: Record<RunState, string> = {
  created: 'Starting',
  running: 'Running',
  // A between-legs stop, not a wait on us: the successor leg is already
  // enqueued, so history reads it as work in progress rather than a pause.
  checkpointed: 'Continuing',
  awaiting_identity_pick: 'Waiting on identity',
  awaiting_prose: 'Waiting on more detail',
  awaiting_adjudication: 'Waiting on review',
  complete: 'Complete',
  failed: 'Failed',
};

/**
 * The run words for a reader who is not the dashboard, tolerant of a token
 * neither map carries.
 *
 * There is no vertical here — a run is a run in every vocabulary — so unlike
 * {@link sourceKindLabel} these take the value alone. The tolerance is the same
 * and is there for the same reason: `RUN_KINDS` and `RUN_STATES` widen (three
 * kinds became four in a year), and a row written under a wider vocabulary than
 * the one this build carries must reach the reader as its token rather than as
 * an `undefined` that drops the key out of the payload.
 */
export function runKindLabel(kind: RunKind): string {
  return (RUN_KIND_LABELS as Record<string, string | undefined>)[kind] ?? kind;
}

export function runStateLabel(state: RunState): string {
  return (RUN_STATE_LABELS as Record<string, string | undefined>)[state] ?? state;
}

/**
 * The **server-set** codes a failed run's `error.code` can carry, named here so
 * the server, the stub and the dashboard branch on one spelling rather than
 * three string literals. A pipeline-reported failure may carry any code (or
 * none), which is why `RunDetail.error.code` stays a plain nullable string —
 * this list is what the surfaces are allowed to *recognise*, not a closed set of
 * everything that can appear.
 *
 * `IDENTITY_WITHDRAWN` is the one that makes a failed run inert history: no
 * strip, no Resume, because resuming would re-research a person the office has
 * just disowned.
 *
 * `SUBJECT_DELETED` is the same shape one step further: the person themselves
 * was deleted while the run was underway, so the run is server-failed inside the
 * deletion transaction and the shred then takes the row with everything else the
 * person keyed. Nothing surfaces it after the commit — the run is gone — but it
 * is what an in-flight invocation's fence and any reader inside that transaction
 * see, and a failure code is how this codebase says *why* a run stopped.
 *
 * `CANCELLED_BY_OFFICE` is the third of that family and the only one somebody
 * asks for on purpose (owner ruling, 2026-09-10: "We should have a cancel button
 * on runs"). A run the office stopped is **not a failure to report on** — it is
 * the answer the office just gave — so it wears the withdrawal's treatment
 * exactly: inert history, no Resume, and no row on the needs-action inbox. The
 * state is `failed` and not a state of its own, for the reason
 * `withdrawActiveRuns` gives: the terminal fence, the partial unique index on a
 * live run, the reaper's sweep and the engine's `_terminal` all read
 * `state NOT IN ('complete','failed')`, and a sixth terminal state would have to
 * be taught to every one of them to say what a code already says.
 */
export const RUN_ERROR_CODES = {
  IDENTITY_WITHDRAWN: 'IDENTITY_WITHDRAWN',
  SUBJECT_DELETED: 'SUBJECT_DELETED',
  CANCELLED_BY_OFFICE: 'CANCELLED_BY_OFFICE',
  /**
   * **A report's prose did not say something the report cannot do without**
   * (reports design ruling 9) — the event's date, for the event advance. The one
   * code the pipeline sets rather than the server: it is only known after the
   * inputs are read, and it is posted before any writer call, so nothing was
   * spent writing. The report page puts its message beside Change inputs.
   */
  REPORT_INPUT_MISSING: 'REPORT_INPUT_MISSING',
  /**
   * **A quick search's candidate merge came back unreadable twice** (2026-09-29).
   * Pipeline-set, like the report code: a defect in the pipeline rather than
   * anything the office can fix, so the message sends them to Arbiter. It is an
   * ordinary failure everywhere else — Resume, the inbox and the strip read it as
   * one.
   */
  IDENTITY_MATCH_FAILED: 'IDENTITY_MATCH_FAILED',
} as const;
export type RunErrorCode = (typeof RUN_ERROR_CODES)[keyof typeof RUN_ERROR_CODES];

export const FACET_MODES = ['full', 'targeted', 'update'] as const;
export const FacetMode = z.enum(FACET_MODES);
export type FacetMode = z.infer<typeof FacetMode>;

export const AUTO_CONTINUE_MODES = ['none', 'research'] as const;
export const AutoContinue = z.enum(AUTO_CONTINUE_MODES);
export type AutoContinue = z.infer<typeof AutoContinue>;

/* ————————————————————————— decisions ————————————————————————— */

/** Kinds that are created `pending` and wait for a human answer. */
export const PENDING_DECISION_KINDS = [
  'identity_pick',
  'identity_more_prose',
  'identity_confirm',
  'claim_adjudication',
  /**
   * **"Is HBS the same as Harvard Business School?"** — the question the
   * resolver records when it mints a new counterparty and the org already holds
   * a live node of the same kind that looks like the same thing (likely-merges
   * note §1c). It is a question and never a guess: nothing binds on the signal,
   * and the pair is asked **once per new spelling per org**, because the second
   * "HBS" of an import matches the first by exact name and never reaches the
   * mint.
   *
   * It is the one pending kind whose subject is not a person the office
   * follows — the subject is the row that was just minted, which is as often an
   * organisation or an event as a person. That is why it is listed apart from
   * the run-and-record items on Home (`needs-action.service.ts`) rather than
   * under a subject heading, and why it is absent from the record's own review
   * counter.
   *
   * "Same" runs the ordinary merge, which leaves a redirect, so every later
   * "HBS" resolves on its own. "Different" is remembered in
   * `entity_pair_verdict`, so the pair is never asked again.
   */
  'entity_merge_candidate',
  /**
   * **One record renamed** (organisations-as-subjects note §2 chunk 1, §2 chunk
   * 3a) — the name it went by, the name it goes by now, which door said so and,
   * from a lookup, what it read that off (`payload: { entity_id, entity_kind,
   * from_display_name, to_display_name, source, evidence }`,
   * {@link EntityRenamePayload}).
   *
   * Its own kind rather than a `correction`, following `status_change`: a
   * correction says the record was *wrong* about the world, and a rename says
   * the record is about the same thing under the name people actually use.
   * Nothing was misdescribed — "Medici's Cancer Shield Program" was the office's
   * own working title — and a reader filtering the log should be able to tell
   * those apart without opening a payload.
   *
   * It is also what the rename's **ledger rows hang on**: `entity_renamed` and
   * `alias_stub_created` are keyed by a decision id (`entity_merge_ledger`), and
   * before this kind existed the only decision that could hold them was the
   * merge that happened to carry a rename with it.
   *
   * **It is a pending kind that is very often filed answered, and both halves
   * are the same row.** The office's own door (chunk 1) writes it with
   * `source: 'office'` and `answer: { confirmed: true }` already on it: a person
   * decided, and the question was never worth asking. It never reaches the
   * needs-action inbox, and not by a rule of its own — that read projects over
   * `status = 'pending'`, which such a row has never been.
   *
   * Chunk 3's lookup writes the *same* kind **pending** with `source: 'lookup'`
   * ("this looks like it is really called Pogue Holdings"), and answering it
   * runs the same rename and writes the same two ledger rows. Which is why the
   * kind lives here rather than in {@link RECORDED_DECISION_KINDS}: the answer
   * path dispatches on this list, and a kind outside it is a 404 at the door.
   *
   * It moved here on 2026-09-09 with that half. `decision.kind` is plain `text`
   * with no check constraint and `DECISION_KINDS` is the two lists together, so
   * the move is a vocabulary change and no migration — but it is **not** nothing:
   * the two projections that file a question under **a person's heading** (the
   * record's review counter, the needs-action entries) exclude it the way they
   * exclude `entity_merge_candidate`, because a name is not a finding about
   * anybody and the record being renamed is as often an organisation as a
   * person. It rides the inbox in a list of its own.
   */
  'entity_rename',
  /**
   * **"Which of these on-chain identities, if any, is this person?"**
   * (docs/notes/2026-10-01-onchain-intel-arkham-spec.md §4.3). A research run's
   * finances leg raises it when a name search or a wallet finds entities that
   * could be the subject and none is on the record yet. It is a pick of one or
   * "none of these", never keep/exclude, and it is not identity: answering it
   * writes a confirmed `onchain_entity` handle and no anchor-set version. It
   * gates the run before its summary, so the brief is written after the
   * chosen identity's wallets are read.
   */
  'onchain_entity_pick',
] as const;
export const PendingDecisionKind = z.enum(PENDING_DECISION_KINDS);
export type PendingDecisionKind = z.infer<typeof PendingDecisionKind>;

/** Kinds that are logged `recorded` — they were never pending (server-design.md §8). */
export const RECORDED_DECISION_KINDS = [
  'identity_bind',
  /**
   * **An on-chain identity stored without asking** (spec §4.3, ruling
   * 2026-10-02): a wallet the office confirmed is verified as belonging to an
   * entity in the subject's own name. The server writes it only where that
   * wallet is a confirmed anchor on the subject; the office can remove it in
   * profile links.
   */
  'onchain_entity_bind',
  'dedup_resolution',
  'claim_attach',
  'correction',
  'negation',
  'sensitivity_change',
  'anchor_accretion',
  'anti_anchor_write',
  /**
   * **"These cards are one person"** — the office checked two or more cards on
   * an `identity_pick` (2026-10-12). The pick row's own answer binds them; this
   * row sits beside it saying what that answer cannot say in one word: the
   * office judged that two sources' candidates described one person. It names
   * the pick, the refs and the names, so a later undo of the joined
   * identification has the whole list to work from. Filed with the user who
   * decided it, never pending. A vocabulary change and not a migration:
   * `decision.kind` has no check constraint.
   */
  'identity_same_person',
  'entity_merge',
  /**
   * **The office folding two records together without being asked to** — "Join
   * with another record", on a person's entry and on a tracked person's own
   * header (organisations-as-subjects note §2 chunk 1, "Join by hand").
   *
   * It is **not** the fold itself. The fold writes its own `entity_merge` row,
   * exactly as it does for the CLI and for the merge card, and this row sits
   * beside it saying the thing that row cannot: *nobody asked*. The likely-merge
   * probe never filed a question about this pair — a bare "Clint" off an import
   * has no trigram tie to "Clint Phillips" — so there is no answered
   * `entity_merge_candidate` in the log, and without this row the only account of
   * how two records came to be one would be a merge nothing led to.
   *
   * Its own kind rather than a flag on the merge's payload, following
   * `fact_merge` and `fact_amendment`: a reader filtering the log for "what did
   * the office decide by hand" should not have to open a payload to find it. It
   * carries the two ids, the two names, which side the office kept, and the
   * `entity_merge` decision it produced — so the log leads from the press to the
   * fold without anybody matching timestamps.
   *
   * Filed **answered** and never pending, through `DecisionsService.answered`:
   * a person decided, and the question was never worth asking. Which is also why
   * it lives here rather than in {@link PENDING_DECISION_KINDS} — no answer path
   * dispatches on it, and no inbox carries it.
   */
  'entity_merge_by_hand',
  'entity_unmerge',
  /**
   * **One person on record who was two, made two** — the couple-cell repair
   * (`entity:split-couple`, operator-tools.md §16; import design note §1.16a).
   *
   * A spreadsheet's name cell reading "Stephen & Erica Malbon" became one
   * subject carrying one household's email, phone and member number. There is no
   * unmerge and there is no entity split, so the record could not be taken apart
   * from any office door: the tool renames the subject to the first name, leaves
   * the couple spelling as a retired redirect, makes the second half a subject
   * of its own, and puts the spouse relation across the two of them.
   *
   * Its own kind rather than an `entity_merge` read backwards, following
   * `fact_merge` and `fact_amendment`: nothing was folded, and a reader filtering
   * the log for what happened to this record should not have to open a payload
   * to find that it was taken apart rather than joined. The payload names the
   * second half, both redirect stubs, the spouse fact and **the identifiers left
   * on the first and why** — the file named both people and did not say whose
   * the member number was, which is the one thing the tool refuses to guess and
   * the one thing a reader will come back for.
   *
   * `decision.kind` is plain `text` with no check constraint, so this is a
   * vocabulary change and not a migration — the same footing `fact_merge` and
   * `dossier_import` arrived on.
   */
  'entity_split',
  /**
   * **One organization on record that was several, made several** — the
   * compound-organization repair (`entity:split-org`, operator-tools.md §18;
   * import design note §1.16b).
   *
   * A board file's employer cell reading "Golden State Warriors & Dallas
   * Mavericks" became one organization, and that organization is the
   * counterparty of a member's `employment`. Answering "different" on the merge
   * card is correct and changes nothing: the junk record is still what the
   * member's record says they work for. The tool attaches or mints one
   * organization per component, re-parents every fact whose counterparty was the
   * compound onto each of them, retires what the compound accreted, and
   * tombstones it **without a redirect** — because a compound is not a spelling
   * of any one component, and a redirect would make the next mention of the
   * whole cell land on whichever half was named first.
   *
   * Its own kind rather than an `entity_split` with an org in it, following that
   * kind's own reasoning one door over: the two say different things about what
   * happened to the record. A couple split leaves the first half standing under
   * its own id with the couple spelling redirecting to it; this leaves **nothing**
   * standing — every component is a different node and the compound is gone. A
   * reader filtering the log should not have to open a payload to tell "this
   * record became two people, one of which is still this row" from "this record
   * was several organizations and is no longer anything".
   *
   * The payload is reversibility-first, as §17's is: the compound as it stood,
   * every component and whether the run minted it, and per fact lineage the
   * whole of the row before — current and pinned version, dedup key,
   * counterparty column, embedding version and fields — beside the lineages and
   * versions that came out of it.
   *
   * `decision.kind` is plain `text` with no check constraint, so this is a
   * vocabulary change and not a migration — the same footing `entity_split`,
   * `fact_merge` and `dossier_import` arrived on.
   */
  'org_split',
  'tombstone',
  /**
   * One operator-run repair of the replay collapse — the census worklist, the
   * versions it repaired and every claim it relitigated, in one row. It is its
   * own kind rather than a `correction` because nothing here says the world was
   * misdescribed: the claims were right and were filed against the wrong facts,
   * and the repair re-ran them through the fixed pipeline. Written by
   * `DedupRepairService`, exactly as `entity_merge` is written by the merge.
   */
  'dedup_repair',
  /**
   * One operator-run fold of a duplicate fact **lineage** into the lineage that
   * survives — the near-key paraphrase cleanup. Its own kind rather than a
   * `dedup_repair` with a discriminator in the payload, following `entity_merge`:
   * the two say different things about the record (the repair re-ran claims
   * through the pipeline and let it decide where they land; this moved a whole
   * lineage's corroboration onto another lineage by hand) and a reader filtering
   * the log should be able to tell them apart without opening a payload.
   * `decision.kind` is plain `text` with no check constraint, so a new kind is a
   * vocabulary change and not a migration. Written by `FactMergeService`, one row
   * per folded pair, carrying `fact_lineage_id` and `fact_type`.
   */
  'fact_merge',
  /**
   * One operator-set **field** on one fact — the amendment. Its own kind rather
   * than a `correction`, following `fact_merge`'s reasoning: a correction is the
   * office saying the record is *wrong* (and the four semantics all suppress or
   * close something), while an amendment says the record is right and one field
   * of it was never written down. The three pre-ladder `records_check` rows that
   * prompted it are the shape: their prose already read clear, `outcome` did not
   * exist when they were extracted, and the absent field — correctly — rates
   * them severe. Nothing about the world changed; what the record *states* did.
   *
   * A reader filtering the log should be able to tell that apart from a
   * correction without opening a payload, and the amended version is **pinned**,
   * which no ordinary claim path does. `decision.kind` is plain `text` with no
   * check constraint, so a new kind is a vocabulary change and not a migration.
   * Written by `FactAmendService`, one row per amended fact, carrying
   * `fact_lineage_id` and `fact_type`.
   */
  'fact_amendment',
  /**
   * One office switch of a subject's status — the word it was, the word it is
   * now (`payload: { from, to }`). Its own kind rather than a `correction`,
   * following the two above: a correction says the record was *wrong*, and this
   * says the relationship moved. Nothing about the person was misdescribed —
   * the prospect became a member, or the committee turned them down — and a
   * reader filtering the log should be able to tell those apart without opening
   * a payload. The row is also what `status_changed_at` is read off, so the log
   * is the history rather than a column beside it.
   *
   * Written by `SubjectsService.setStatus`, in the same transaction as the
   * vertical's own row. The words in the payload are the vertical's vocabulary,
   * so nothing core labels them.
   */
  'status_change',
  /**
   * One import session's commit — the whole of what a file wrote, in one row
   * (import note §1.9). It is **the unit of undo**, which is why it is a decision
   * rather than a flag on the session: every entity, anchor, claim, fact and
   * prose input the commit produced points at it, so "what did this file do" and
   * "take it back" are one question asked of one record.
   *
   * It is also the **approval record** for everything it wrote. Import-origin
   * claims skip `evaluateGate` and land `auto_keep` (§1.10, the roadmap's
   * backfill posture), and this row is what makes that legitimate: the office
   * approved the file, once, in front of a preview that named every count.
   *
   * Written by the executor at the head of the structural phase, before the first
   * chunked transaction, so a crash resumes against a decision that already
   * exists.
   */
  'import_commit',
  /**
   * One import session's unwind — committed, then taken back (§1.9, §4.1). Its
   * own kind rather than a `correction` or a `tombstone`, following
   * `entity_merge`: a correction says the record was wrong about the world, and
   * this says a *file* was wrong to have been imported at all. It carries what it
   * removed and, because the settled rule is **stop and report**, the people it
   * did not: a subject worked on since the commit (a note, a run, a status
   * change) is kept, listed, and left to the existing shred.
   */
  'import_unwind',
  /**
   * **One hand-written dossier package, written down** — `dossier:import`, the
   * one-off operator tool that turns an Overwatch dossier reformatted into the
   * fact catalog's own vocabulary into a tracked prospect
   * (`docs/notes/2026-09-08-overwatch-dossier-import.md`, `docs/operator-tools.md`
   * §14). One row per package, plus one for the batch, which is the session's
   * `commit_decision_id`.
   *
   * **Its own kind rather than an `import_commit`**, and the difference is the
   * whole of what `import_commit` means: a named person pressed a button in front
   * of a preview that counted every fact the file was about to write, which is
   * what makes that decision the approval record behind `bulkApproved`. There is
   * no preview and no button here — an operator ran a command at a terminal
   * against files they wrote by hand — and filing that under the same word would
   * put an approval nobody gave into the log. The claims this writes carry
   * `import_id` and **not** `bulkApproved`, so the three reviewed types
   * (`IMPORT_REVIEWED_FACT_TYPES`) queue for a look exactly as they should.
   *
   * Recorded with `actor: 'user'` and `answered_by_user_id` null, like every
   * other operator CLI (`operator-tools.md` §1): the tool authenticates to the
   * database, not to the dashboard, and the payload says `operator:
   * 'cli:dossier-import'`. The `--author` user it names is the `prose_input`'s
   * author and the run creator, which is a different question from who decided.
   */
  'dossier_import',
  /**
   * **The office saying what it knows about an organisation** (organisations-as-
   * subjects note §2 chunk 2a, pass ii) — the website it is at, and the place or
   * jurisdiction its own words name (`payload: { entity_id, statements: [{kind,
   * value}] }`, {@link OfficeStatementPayload}).
   *
   * It exists because of what an anchor's own CHECK requires: a `confirmed`
   * anchor must name the decision that confirmed it (`anchor.entity.ts`), and
   * until now every confirmed anchor in this system came from a person's
   * `identity_pick` or an identity lock. An organisation has neither — it has an
   * office typing a website into a form — so the confirmation needed a decision
   * of its own to hang on, and this is it. Written already answered by
   * `DecisionsService.answered`, actor `user`, attributed to whoever pressed the
   * button.
   *
   * Its own kind rather than an `anchor_accretion`, which is what the pipeline
   * writes when a run's evidence earns an anchor: that row says *the research
   * found this*, and this one says *the office states it*. They differ in who is
   * answerable for the value, which is the whole of what a confirmed anchor
   * asserts, and a reader filtering the log should be able to tell them apart
   * without opening a payload.
   *
   * **A statement with no statements in it is a legitimate row**: the office
   * wrote a paragraph about the organisation and the text named neither a place
   * nor a jurisdiction. What they did is still on the record — the prose input is
   * the substance — and the log says they added what they know.
   */
  'office_statement',
  /**
   * **The office stopping a run it had started** — "Cancel" beside the status
   * line (owner ruling, 2026-09-10, after a ~$3 investigation was pressed on an
   * organisation before a fix had deployed and there was no way to stop it).
   *
   * Its own kind rather than a `correction`, following `status_change` and
   * `entity_merge_by_hand`: a correction says the record was *wrong* about the
   * world, and nothing here says that. The run read what it read, every claim it
   * had already landed stays on the record, and what changed is that the office
   * decided it had seen enough. A reader filtering the log for "what did somebody
   * stop by hand" should not have to open a payload to find it.
   *
   * Filed **answered** through `DecisionsService.answered` rather than
   * `recorded`: a person decided, the row carries their id and the moment they
   * pressed it, and there was never a question to ask. It carries the state the
   * run was in when it was stopped (`payload: { from_state }`), which is the one
   * thing the row cannot be reconstructed from afterwards — the run is `failed`
   * for ever after.
   */
  'run_cancelled',
  /**
   * **The office ruling on one of a person's accounts** — confirming an X or
   * Bluesky account a note named, setting it aside, or removing one confirmed
   * earlier (`payload: { entity_id, anchor_id, action, platform, handle }`,
   * {@link ProfileLinkPayload}). A confirmed anchor must name the decision that
   * confirmed it, and a retired one the decision that retired it.
   *
   * Its own kind rather than an `office_statement`: that row says what the office
   * knows about an organisation, and this one answers a suggestion. Neither
   * establishes who the person is.
   */
  'profile_link',
  /**
   * **An owner rewriting a note on a person's record** (2026-10-29, after a
   * club's notes named the wrong person and an imported "Club Fit" note needed
   * correcting). The note's rows are taken out of every reader and the new
   * words are filed as rows of their own, carrying the original's author,
   * dates and confidentiality (`payload: { entity_id, removed_input_ids,
   * filed_input_ids }`, {@link NoteChangePayload}).
   *
   * It is also what the withdrawal hangs on. The new words are read first and
   * the old words' claims are withdrawn when that reading lands, so a fact the
   * new text still states is never negated on the way past; a fact only the old
   * words supported is negated `note_changed` by this decision.
   */
  'note_edit',
  /**
   * **An owner removing a note from a person's record** (2026-10-29). The rows
   * are kept for the audit trail and out of every reader, the way `notes:split`
   * keeps a parent, and what the note alone supported is withdrawn in the same
   * transaction (`payload: { entity_id, removed_input_ids }`).
   */
  'note_removal',
] as const;

export const DECISION_KINDS = [...PENDING_DECISION_KINDS, ...RECORDED_DECISION_KINDS] as const;
export const DecisionKind = z.enum(DECISION_KINDS);
export type DecisionKind = z.infer<typeof DecisionKind>;

export const DECISION_STATUSES = ['pending', 'answered', 'recorded'] as const;
export const DecisionStatus = z.enum(DECISION_STATUSES);
export type DecisionStatus = z.infer<typeof DecisionStatus>;

export const DECISION_ACTORS = ['pipeline', 'server', 'server_llm', 'user'] as const;
export const DecisionActor = z.enum(DECISION_ACTORS);
export type DecisionActor = z.infer<typeof DecisionActor>;

/**
 * Which branch produced an `identity_more_prose` row — the prompt's headline
 * depends on it, and since the three ask tiers (2026-09-03, Soroush) so does
 * whether the office is offered anything to press.
 *
 * The tiers are read off the **live candidate count**: 1–5 is an `identity_pick`
 * and never reaches this vocabulary; 6–15 is `over_five`, whose
 * `retained_candidates[]` are **bindable** — the held list carries a "Select this
 * person" shortcut on every row, and answering with that candidate's ref runs the
 * pick's own binding path; past 15 is `over_fifteen`, which retains nothing
 * (`retained_candidates: []`, the count in `candidate_count`) because a list that
 * long is not a list anybody reads, and prose is the only way through.
 *
 * `zero` and `none_of_these` are unchanged and carry no bindable candidates by
 * construction — one found nothing, the other just rejected everything it found.
 */
export const MORE_PROSE_ORIGINS = ['over_five', 'over_fifteen', 'zero', 'none_of_these'] as const;
export const MoreProseOrigin = z.enum(MORE_PROSE_ORIGINS);
export type MoreProseOrigin = z.infer<typeof MoreProseOrigin>;

export const ADJUDICATION_SENSITIVITIES = ['low', 'medium', 'high'] as const;
export const AdjudicationSensitivity = z.enum(ADJUDICATION_SENSITIVITIES);
export type AdjudicationSensitivity = z.infer<typeof AdjudicationSensitivity>;

/* ————————————————————————— identity anchors ————————————————————————— */

/**
 * The anchor kinds a **person** carries (`anchor.kind`, data-model invariant 8).
 * The column is plain `text` with no CHECK, so this is the vocabulary the
 * writers agree on rather than a constraint the database enforces — an anchor of
 * some other kind still stores, and the stub extractor's heuristic proposes
 * `aka` for everything it is unsure of.
 *
 * `deceased` joined on 2026-09-03 (Soroush) and is the one kind here that does
 * not describe *which* person this is. It is a fact about the person the
 * pipeline needs before it starts: a candidate set for a name is full of dead
 * namesakes, and the pipeline sets those aside — so a subject the office has
 * told us is dead must be exempt from that set-aside, or the run would rule out
 * the very person it was asked about. It rides as an anchor because that is the
 * channel the office's own knowledge already reaches the pipeline through
 * (`candidate_anchors[]` → `anchor(status='candidate')` → the context read), and
 * a fact would have to be extracted, gated and resolved first.
 *
 * **Never inferred.** Both intake prompts emit it only when the note *says* the
 * person has died or gives a date of death; "his widow" and a past-tense verb
 * are not it.
 *
 * **The four identifiers joined on 2026-09-06 with the import module** (§1.5):
 * `email`, `phone`, `member_number` and `external_id`. They are a different sort
 * of anchor from the eight above and the difference is worth naming — the others
 * *describe* a person well enough to tell them from a namesake, and these
 * **are** the person's identity in some system: a row's identity is its
 * identifier, not its name, so the same identifier is one person and the same
 * name with two identifiers is two people, no question asked.
 *
 * That is what makes them worth writing `confirmed` from a first-party file, and
 * what makes a later export of the same board attach rather than duplicate. Their
 * values are the one other thing the server settles rather than stores
 * (`server/src/modules/identity/anchor-value.ts`): an email lowercased, a phone
 * reduced to digits, a member number exactly as the club writes it, and an
 * external id as `{namespace, id}` — because a comparison is what they exist for,
 * and two spellings of one address would compare unequal.
 */
export const PERSON_ANCHOR_KINDS = [
  'role_org',
  'location',
  'affiliation',
  'known_to',
  'handle',
  'education',
  'aka',
  'deceased',
  'email',
  'phone',
  'member_number',
  'external_id',
] as const;
export const PersonAnchorKind = z.enum(PERSON_ANCHOR_KINDS);
export type PersonAnchorKind = z.infer<typeof PersonAnchorKind>;

/** The org anchor kinds, for the org anchor sets (design.md). */
export const ORG_ANCHOR_KINDS = [
  'registry_id',
  'jurisdiction',
  'domain',
  'location',
  'associated_person',
] as const;

/** The `deceased` anchor's kind name, spelled once. */
export const DECEASED_ANCHOR_KIND = 'deceased';

/**
 * **The `domain` anchor's kind name**, spelled once, because three places have
 * to agree on it (organisations-as-subjects note §2 chunk 2a "As grounded" 2):
 * the office statement that writes one `confirmed`, the resolver's own probe that
 * looks an organisation up by it, and `normalizeAnchorValue`, which is what makes
 * the two land on one spelling of one host.
 *
 * It is the sixth settled value in this system and it is settled for the reason
 * the other five are: something reads it **structurally**. `medici.org`,
 * `https://www.Medici.org/about` and `MEDICI.ORG.` are one organisation's site
 * written three ways, and a probe comparing them as text finds nothing.
 */
export const DOMAIN_ANCHOR_KIND = 'domain';

/**
 * **The `location` anchor's kind name**, spelled once for the same reason
 * `DOMAIN_ANCHOR_KIND` is (organisations-as-subjects note §2 chunk 4, "As
 * built"). It began life local to `org-anchor-match.ts`, on the ground that only
 * that file and the resolver read it; the organisation record's own reader
 * (`organisation-record.service.ts`, a different module) is the third, and a
 * kind name three files must agree on belongs where the other five settled
 * values are.
 *
 * Unlike `domain` nothing on the wire is *typed* by it — it is one of the org
 * anchor kinds the extractor may surface, and its value is a place written
 * however the writer wrote it.
 */
export const LOCATION_ANCHOR_KIND = 'location';

/**
 * The `deceased` anchor's value: **a date or the honest absence of one**. A note
 * saying he died in 2019 carries the year; a note saying only that he has died
 * carries `null`, which is a different thing from the anchor not being there at
 * all. The string is whatever grain the note gave (`2019`, `2019-04`,
 * `2019-04-12`) — the same loose calendar shape a `content_date` carries.
 */
export const DeceasedAnchorValue = z.object({ date: z.string().nullable() });
export type DeceasedAnchorValue = z.infer<typeof DeceasedAnchorValue>;

/**
 * **The four identifier anchors' kinds and settled value shapes** (import note
 * §1.5, 2026-09-06).
 *
 * They are settled rather than stored for one reason: everything else in an
 * anchor value is descriptive text nothing reads structurally, and these exist to
 * be **compared**. `Lew@Example.com ` and `lew@example.com` are one address and
 * must be one anchor; a phone the board exported as an integer and one a note
 * gives as `+1 (305) 555-0134` are one number. So the shape and the spelling are
 * forced on the way in (`anchor-value.ts`), and every writer — the import
 * executor, the intake extractor's candidate anchors — lands the same row.
 *
 * `null` is what a value with nothing usable in it becomes, exactly as a
 * `deceased` anchor with no date does: an anchor that identifies nobody, visible
 * as such, rather than a differently-shaped one that quietly matches nothing.
 */
export const EMAIL_ANCHOR_KIND = 'email';
export const PHONE_ANCHOR_KIND = 'phone';
export const MEMBER_NUMBER_ANCHOR_KIND = 'member_number';
export const EXTERNAL_ID_ANCHOR_KIND = 'external_id';

/** Lowercased and trimmed — the address is the identity, its casing is not. */
export const EmailAnchorValue = z.object({ email: z.string().nullable() });
export type EmailAnchorValue = z.infer<typeof EmailAnchorValue>;

/**
 * E.164 where the number parses — a note's `+1 (305) 555-0134` and an export's
 * `3055550134` are both `+13055550134`, and an export's `525512345678` is
 * `+525512345678` — and the pre-E.164 digits where it does not, so a 7-digit
 * local number is `5550134` (server `identity/phone.ts`; country routing
 * foundation §4).
 */
export const PhoneAnchorValue = z.object({ phone: z.string().nullable() });
export type PhoneAnchorValue = z.infer<typeof PhoneAnchorValue>;

/**
 * **As the club writes it**, trimmed and otherwise untouched: a member number is
 * a string in the club's own scheme, zero padding and all, and normalising it
 * would be us deciding that `0142` and `142` are the same member — which is the
 * club's fact, not ours.
 */
export const MemberNumberAnchorValue = z.object({ member_number: z.string().nullable() });
export type MemberNumberAnchorValue = z.infer<typeof MemberNumberAnchorValue>;

/**
 * An id in a named foreign system: `{namespace: 'monday:acme', id: '4821'}`.
 *
 * The namespace is lowercased because it is ours — a label the plan chooses, and
 * two spellings of it would split one board's people in half. The id is left
 * exactly as the system wrote it, because it is theirs and may well be
 * case-sensitive.
 */
export const ExternalIdAnchorValue = z.object({
  namespace: z.string().nullable(),
  id: z.string().nullable(),
});
export type ExternalIdAnchorValue = z.infer<typeof ExternalIdAnchorValue>;

/* ————————————————————————— facts & claims ————————————————————————— */

export const FACT_STATUSES = ['active', 'superseded', 'negated'] as const;
export const FactStatus = z.enum(FACT_STATUSES);
export type FactStatus = z.infer<typeof FactStatus>;

/**
 * `wrong_person` joins the reason codes (2026-08-26): a wrong-person correction
 * negates the lineage with that reason and nulls its payload to the skeleton, so
 * the greyed "removed, wrong person" stub renders from status + reason exactly
 * like every other negation.
 */
export const NEGATION_REASONS = [
  'wrong_fact',
  'no_longer_true',
  'do_not_surface',
  'wrong_person',
  /**
   * The fact came from an import session that has since been **unwound** (import
   * note §1.9, 2026-09-06). Its own reason rather than `wrong_fact`, on the rule
   * the other three follow: nothing here says the world was misdescribed — the
   * file may have been perfectly accurate — it says the *import* was taken back,
   * and a reader looking at a greyed line deserves to be told which of those
   * happened.
   *
   * **Written only by the unwind, and the unwind reaches a fact through its
   * claim.** `fact` has no `import_id` column; `claim` does, and a fact's lineage
   * is its claims — so what the unwind walks is the session's claims and the
   * facts they wrote.
   *
   * That is a rule for the executor chunk rather than a remark about this word:
   * **every fact an import writes goes through a claim carrying the session's
   * `import_id`.** The facts a column target produces, the facts a note's
   * extraction produces, and the deterministic ones the plan writes with no model
   * in the loop at all — the `family_relation(spouse)` a couple heading yields
   * (§1.16) included. A pair fact written straight onto the record with no claim
   * behind it is a fact the unwind cannot see: it would outlive the session that
   * made it, with nothing anywhere to say where it came from.
   *
   * The negation itself is a negation like any other: the fact stays visible with
   * its reason, because removed things stay visible.
   */
  'unwound',
  /**
   * The statement was **true when it was made and has been superseded by finer
   * facts that stand on the record** (2026-09-16). Nothing was wrong about the
   * world and nothing is hidden: the reader is told the finer rows are where the
   * statement now lives.
   *
   * **Written only by `entity:split-org`** (operator-tools §18), where a compound
   * organization's lineage is retired because every component already carries the
   * statement. "Works at Golden State Warriors & Dallas Mavericks" was never
   * false; it was coarse, and both halves of it are now standing on their own
   * organizations.
   *
   * **Not `wrong_fact`**, which is what that negation wrote until today: that
   * word says this row got the world wrong, and the greyed line then read
   * "removed as wrong" about a statement nobody ever disputed.
   *
   * **Not `do_not_surface`** either, which reads closer and cannot be used:
   * `highestNegatedLookalike` (`resolution.service.ts`) inherits that reason and
   * only that reason, and the compound's row keeps a vector rendered from a name
   * containing every component's — so the next honest employment at one of the
   * components is exactly the lookalike it would auto-suppress. This word is
   * never inherited, and the allow-list there stays an allow-list.
   *
   * The readers, all of them: never inherited by a lookalike; never resurrected
   * (a later claim attaches `post_negation` and the lineage stays negated —
   * `facts.service.ts`, reason-blind on purpose, because the compound spelling
   * stays retired while the components carry the truth); absent from prompts,
   * facets, prose and briefs by **status** rather than by reason, so nothing
   * there needed a word for it; and greyed on the record with its own label
   * ("replaced by finer facts").
   *
   * **Not in `CORRECTION_SEMANTICS`**: the office has no door that writes it. It
   * is an operator repair's word for what the repair did.
   */
  'replaced',
  /**
   * **The note the fact was read from was edited or removed** (2026-10-29), and
   * nothing else on the record supports it. Written only by `note_edit` and
   * `note_removal`. Its own word because neither of the others is true: the
   * note may have been about the wrong person (`wrong_person` would say the
   * office knows that, and the owner only said the note was wrong to be here),
   * or simply mistyped. Sticky like every negation, so a later note saying the
   * same thing attaches without resurrecting; the edit is ordered so the new
   * text's own reading lands before the old text's claims are withdrawn.
   */
  'note_changed',
] as const;
export const NegationReason = z.enum(NEGATION_REASONS);
export type NegationReason = z.infer<typeof NegationReason>;

export const CORRECTION_SEMANTICS = [
  'wrong_person',
  'wrong_fact',
  'no_longer_true',
  'do_not_surface',
  /**
   * "Part of this is wrong" (2026-09-30): named plain fields rewritten, the rest
   * kept. The one semantic that neither negates nor closes — it writes a pinned
   * office version (`FIXABLE_FIELDS` in `corrections.ts`).
   */
  'fix',
] as const;
export const CorrectionSemantic = z.enum(CORRECTION_SEMANTICS);
export type CorrectionSemantic = z.infer<typeof CorrectionSemantic>;

export const SENSITIVITIES = ['standard', 'sensitive'] as const;
export const Sensitivity = z.enum(SENSITIVITIES);
export type Sensitivity = z.infer<typeof Sensitivity>;

/**
 * Severity is a server-computed class over the fact's type and fields
 * (server-design.md §7.1). Four classes are user-facing and the fifth is the
 * absence of one: `severe` is the amber reading, `moderate` says the subject is
 * on the quieter side of an adverse item or the check turned up a small thing,
 * `info` says the check recorded something that is **not adverse** (the
 * subject's own public self-disclosure, no allegation and no proceeding),
 * `none` says the check ran and found nothing (a clean result rendered as
 * clean, not as unlabelled), and the server's `normal` — a fact with no valence
 * at all — renders as nothing, so it crosses the wire as `null`. Content-aware
 * since §3c/§3h: a witness appearance and a suit against the subject are not
 * the same weight. There is no numeric anything.
 *
 * `info` (2026-09-02) is a class, not a shade of `moderate`: the dashboard
 * treats the two oppositely — `moderate` is gathered into "Anything to know",
 * `info` deliberately is not — and neither may be counted as a clean check.
 * Nothing here is ordered; every consumer reads this by equality.
 */
export const Severity = z.enum(['severe', 'moderate', 'info', 'none']).nullable();
export type Severity = z.infer<typeof Severity>;

export const SOURCE_TIERS = [
  'registry_filing',
  'major_press',
  'trade_press',
  'social',
  'model_derived',
  'office_correction',
  'office_observation',
  'club_supplied',
] as const;
export const SourceTier = z.enum(SOURCE_TIERS);
export type SourceTier = z.infer<typeof SourceTier>;

/**
 * The tier words every org reads the same, and the neutral wording for the one
 * that does not.
 *
 * `club_supplied` keeps its token (verticals design note §0.6): it sits in two
 * closed shared enums and a Postgres CHECK, so renaming it to `org_supplied`
 * would be a production data rewrite plus an agent catalog-digest re-pin, for
 * cosmetics. The wart is confined to the tokens, and the *label* is what the
 * office reads — the label here is the neutral one, true of any org, so a
 * vertical this build does not carry still gets a sentence that is correct
 * rather than one about a club it does not have.
 *
 * **In `@ni/shared` since 2026-09-10**, lifted out of `dashboard/src/lib/copy.ts`
 * because there are two surfaces now: the dashboard prints these, and the MCP
 * connector reads them to a model that repeats whatever token it is handed. One
 * vocabulary or two wordings for the same tier, and two was never the answer.
 */
export const SOURCE_TIER_LABELS: Record<SourceTier, string> = {
  registry_filing: 'registry filing',
  major_press: 'press',
  trade_press: 'trade press',
  social: 'social',
  model_derived: 'read from other findings',
  office_correction: 'office correction',
  office_observation: 'office note',
  club_supplied: 'supplied by the office',
};

/**
 * Where a vertical says a tier differently. Only the first-party tier is in
 * here, and only because the org that supplied the finding has a name in the
 * office's own head: a club calls itself the club.
 *
 * Keyed by `org.vertical` exactly as the wire spells it, and an entry is an
 * *override* rather than a replacement — a vertical that says nothing about a
 * tier gets the neutral word above, so adding a vertical is one optional line
 * and never a full table to keep in sync.
 */
export const SOURCE_TIER_LABELS_BY_VERTICAL: Readonly<
  Record<string, Readonly<Partial<Record<SourceTier, string>>>>
> = {
  club: { club_supplied: 'supplied by the club' },
  /* **"Reported by the firm", and the word doing the work is *reported*.** A
     protective-intelligence firm's own material is often an allegation somebody
     made to it — a threatening letter, a visitor-log entry, what a client's
     staff believes about a person — and the neutral "supplied by the office"
     reads as the office vouching for it. The vertical's prompts treat
     firm-reported concerns as context to verify and never as conduct to assert,
     and this label is the same statement where a reader sees it. The token stays
     `club_supplied`: it is the pipeline's name for first-party material and
     nothing about it is club-specific but the spelling. */
  security: { club_supplied: 'reported by the firm' },
};

/**
 * What to print for a claim's source tier in **this org's** words.
 *
 * Unlike `subjectStatusLabel`, this never answers null: a source tier is a
 * closed shared enum that every vertical shares, so there is always an honest
 * neutral word for it. The vertical only changes how the first-party one reads.
 *
 * **The lookup is deliberately tolerant** (2026-09-10). The parameter is the
 * union and the map is total over it, so a caller that type-checks can never
 * miss — but the values reaching this function come off `claim.source_tier`, a
 * Postgres column whose CHECK has been widened before and could carry a row
 * written under an older vocabulary. `Record<SourceTier, string>` would hand
 * such a row back as `undefined`, and `undefined` on this lane is a dropped key
 * in the model's payload: the source silently loses its tier rather than
 * showing one the office does not have a word for yet. The token itself is the
 * honest answer to that, and it is the same answer the dashboard's Data tab
 * gives — ugly, and never a hole.
 */
export function sourceTierLabel(vertical: string, tier: SourceTier): string {
  return (
    SOURCE_TIER_LABELS_BY_VERTICAL[vertical]?.[tier] ??
    (SOURCE_TIER_LABELS as Record<string, string | undefined>)[tier] ??
    tier
  );
}

export const SOURCE_KINDS = [
  'osint_run',
  'intake',
  'staff_note',
  'reference',
  'interaction',
  'monitoring',
  'club_supplied',
  'office_edit',
] as const;
export const SourceKind = z.enum(SOURCE_KINDS);
export type SourceKind = z.infer<typeof SourceKind>;

/**
 * **How a claim reached the record, in the office's words** (2026-09-10).
 *
 * The tokens are the pipeline's and stay the pipeline's, for the same reason
 * `club_supplied` does above. What changed is who reads them: the MCP connector
 * hands each fact's sources to a model that repeats whatever string it is
 * given, so a raw `osint_run` came back out of Claude as "OSINT" in a sentence
 * to somebody in the office. Nobody in the office says OSINT. Every label here
 * is a phrase a person would use out loud — "web research", "a staff note" —
 * and none of them carries an underscore or a piece of tradecraft vocabulary.
 *
 * Total over the enum, so a kind added to `SOURCE_KINDS` is a compile error
 * here rather than a token quietly reaching a reader.
 */
export const SOURCE_KIND_LABELS: Record<SourceKind, string> = {
  osint_run: 'web research',
  intake: 'intake form',
  staff_note: 'staff note',
  reference: 'reference',
  interaction: 'logged interaction',
  monitoring: 'monitoring',
  club_supplied: 'supplied by the office',
  office_edit: 'office edit',
};

/**
 * The one kind a vertical says its own way — the same override shape, and the
 * same single entry, as {@link SOURCE_TIER_LABELS_BY_VERTICAL}: a club calls
 * itself the club, and every other vertical gets the neutral word.
 */
export const SOURCE_KIND_LABELS_BY_VERTICAL: Readonly<
  Record<string, Readonly<Partial<Record<SourceKind, string>>>>
> = {
  club: { club_supplied: 'supplied by the club' },
  /* The same relabel as the tier above, and for the same reason: what the firm
     handed us is what the firm reported, not what the record establishes. */
  security: { club_supplied: 'reported by the firm' },
};

/**
 * What to say for a claim's source kind in **this org's** words — tolerant of a
 * token the map has never heard of, for the reason written on
 * {@link sourceTierLabel}: a legacy row outside the enum reaches the reader as a
 * token, never as a key that quietly went missing.
 */
export function sourceKindLabel(vertical: string, kind: SourceKind): string {
  return (
    SOURCE_KIND_LABELS_BY_VERTICAL[vertical]?.[kind] ??
    (SOURCE_KIND_LABELS as Record<string, string | undefined>)[kind] ??
    kind
  );
}

/**
 * **Which verticals offer a confidential note** (ruled 2026-09-19): a note the
 * office keeps to itself, stored and shown on the record but never shipped to a
 * research run or read into claims.
 *
 * Keyed by `org.vertical` as the wire spells it, and the **source** rather than
 * a client index: `Vertical.offersConfidentialNotes` on the server reads this
 * same map, so the add box's checkbox and the server's refusal cannot disagree.
 * A club is the one vertical that needs it today — a member confides in the
 * head of membership, and that sentence must not be what a search is seeded
 * with. A vertical absent here does not offer it.
 */
export const CONFIDENTIAL_NOTES_BY_VERTICAL: Readonly<Record<string, boolean>> = Object.freeze({
  club: true,
});

export function verticalOffersConfidentialNotes(vertical: string): boolean {
  return CONFIDENTIAL_NOTES_BY_VERTICAL[vertical] === true;
}

/**
 * **The source kinds whose evidence is the office's own word** — everything the
 * club told us, however it reached us: an intake form, a staff note, a
 * reference, a logged interaction, a club-supplied column, an office edit. The
 * complement is the two the club did *not* say: `osint_run` and `monitoring`.
 *
 * It lives in the shared vocabulary rather than beside its first reader because
 * two server layers now ask the same question of a claim and one of them cannot
 * see the other: claims force these to the `contextual`/`strong` binding shape
 * (§7.2 step 3, which is what makes `blocking` unreachable for first-party
 * material), and the fact writer reads the same list to decide a new lineage's
 * sensitivity (2026-09-07 — see `sensitivityForSource`). `facts` is upstream of
 * `claims` in the DI graph, so the constant could not stay in `claims` without
 * inverting it.
 */
export const FIRST_PARTY_SOURCE_KINDS = [
  'intake',
  'staff_note',
  'reference',
  'interaction',
  'club_supplied',
  'office_edit',
] as const satisfies readonly SourceKind[];

export const isFirstPartySource = (kind: string): boolean =>
  (FIRST_PARTY_SOURCE_KINDS as readonly string[]).includes(kind);

export const CONTENT_DATE_BASES = ['explicit', 'inferred', 'absent'] as const;
export const ContentDateBasis = z.enum(CONTENT_DATE_BASES);
export type ContentDateBasis = z.infer<typeof ContentDateBasis>;

export const BINDING_STRENGTHS = ['strong', 'moderate', 'name_only'] as const;
export const BindingStrength = z.enum(BINDING_STRENGTHS);
export type BindingStrength = z.infer<typeof BindingStrength>;

/**
 * How an anchor matched a candidate. Open vocabulary — the only value the
 * contract pins today is `contextual` (server-design.md §4, intake binding);
 * search-derived kinds are the pipeline's to name. Qualitative always: a match
 * kind is a word, never a score.
 */
export const MatchKind = z.string();
export type MatchKind = z.infer<typeof MatchKind>;

/* ————————————————————————— prose & subjects ————————————————————————— */

export const PROSE_INPUT_KINDS = [
  'intake',
  'note',
  'reference',
  'more_prose',
  'interaction',
] as const;
export const ProseInputKind = z.enum(PROSE_INPUT_KINDS);
export type ProseInputKind = z.infer<typeof ProseInputKind>;

export const EXTRACTION_STATUSES = ['none', 'pending', 'done', 'failed'] as const;
export const ExtractionStatus = z.enum(EXTRACTION_STATUSES);
export type ExtractionStatus = z.infer<typeof ExtractionStatus>;

/**
 * **Status is a universal invariant of subjects** (data-model.md §3a /
 * server-design.md §7.9a): *the subject's current relationship to the org*.
 *
 *  - **One value per subject, always present.** There is no such thing as a
 *    subject without a status, so a read that cannot find one is a broken
 *    invariant and says so — it never fabricates a default.
 *  - **The vocabulary is the vertical's**, and so is the table it is stored in
 *    (club: `club_subject`). It is never on the core person record.
 *  - **Never workflow state.** `researched`, `archived` and their kind are facts
 *    about runs and flags; they do not describe a relationship and do not belong
 *    here.
 *
 * A vertical with a single value is a full citizen. Adding a value is additive
 * (const + label + derivation case + a generated CHECK migration on the
 * vertical's own table); renaming or removing one is a data rewrite.
 *
 * **Not write-once.** It was until 2026-09-05 — the create transaction was the
 * only writer — and `POST /orgs/:orgId/subjects/:id/status` is the switch now:
 * any word of the vertical's vocabulary to any other, no rule about the order,
 * a `status_change` decision recording who moved it and from what. Nothing
 * already on the record changes with it; the next run derives its kinds and its
 * depth from the new word (`docs/notes/2026-09-05-club-subject-statuses-design.md`).
 */
export const CLUB_SUBJECT_STATUSES = ['prospect', 'member', 'contact', 'declined'] as const;

/**
 * **The whole vocabulary, live since 2026-09-05** (club-statuses note §1.1).
 *
 * It was `['prospect']` for the whole of stage 1, which is what greyed Member
 * and Contact on the new-person form and made the other words 400
 * `STATUS_NOT_YET_SUPPORTED` at the create door. Nothing is held back now: a
 * club files people it has admitted, people it merely knows, and prospects it
 * turned down, and all four are words about the same relationship.
 *
 * Kept as its own export rather than folded away, because the *mechanism* — a
 * vertical whose vocabulary is wider than what it accepts today — is what the
 * form and `assertStatusSupported` are written against, and the next vertical
 * to hold something back needs it standing.
 */
export const CLUB_SUBJECT_STATUSES_LIVE = CLUB_SUBJECT_STATUSES;

/**
 * **The business vertical's vocabulary** (verticals note §0.5): the relationship
 * a small operating business has with someone it knows — an `intro` it was
 * given, a `friend` it stays in touch with, a `partner` it does work with.
 *
 * The key names the *relationship shape*, never the customer's size: a large
 * firm tracking intros and partners uses these same three words, and a firm
 * running a deal pipeline is a different shape that would get its own vertical.
 */
export const BUSINESS_SUBJECT_STATUSES = ['intro', 'friend', 'partner'] as const;

/**
 * All three, live from the first day. Unlike the club — which carries two words
 * its stage-1 build does not accept yet — this vertical holds nothing back, so
 * the new-person form shows no "not yet" line under its status control.
 */
export const BUSINESS_SUBJECT_STATUSES_LIVE = BUSINESS_SUBJECT_STATUSES;

/**
 * **The personal vertical's vocabulary** (personal-vertical note, 2026-09-09):
 * one word, and that is the design rather than a stage-1 restriction.
 *
 * The customer is a person or the office around one, researching the people they
 * meet. The reason for looking travels in the intake note — a potential business
 * partner, a doctor, an inbound pitch — and how close somebody is is a reading
 * the model makes from the notes the office types, not a word the office picks
 * off a control. A ladder here would be a ladder nobody climbs.
 *
 * The token is `contact`, which the **club also has**, and the two are not the
 * same word twice by accident: a club's contact is somebody in its network who
 * is not a membership candidate, and this vertical's is simply somebody on
 * record. That the meaning is per-vertical is exactly why nothing in this file
 * is keyed by status alone (see {@link SUBJECT_VOCABULARY_BY_VERTICAL}).
 */
export const PERSONAL_SUBJECT_STATUSES = ['contact'] as const;

/** The one word, live from the first day: nothing is held back. */
export const PERSONAL_SUBJECT_STATUSES_LIVE = PERSONAL_SUBJECT_STATUSES;

/**
 * **The wealth vertical's vocabulary** (wealth-vertical note, 2026-09-15, ruled
 * 2026-09-16): family offices and wealth managers, which are two seats reading a
 * person the same way and differing only in what they call an established
 * relationship.
 *
 *  - `prospect` — someone the office is deciding whether to pursue or take a
 *    meeting with. The one word that gets the office's own read (`wealth_fit`),
 *    because it is the one word a decision is pending on.
 *  - `client` — an established relationship: a client family on the manager's
 *    side, a manager or co-investor the family is in business with on the other.
 *  - `contact` — someone on record who is neither: a lawyer, an accountant or a
 *    banker who sends people, a counterparty met once, a name from a conference.
 *
 * `prospect` and `contact` are the club's tokens and `contact` is also the
 * personal vertical's; `client` is the one word this vertical adds to the
 * deduped list below. That three verticals share a spelling is ordinary — the
 * meaning is the vertical's, which is why nothing here is keyed by status alone.
 *
 * Held back deliberately and additive when a customer wants them: `partner` (the
 * family-office word for an established counterparty, if `client` ever reads
 * wrong for a manager the family invested with) and `declined` (a pass worth not
 * re-researching). Neither is in this list, in the CHECK, in a label table or in
 * the glossary, because a word a build carries but never hands out is a word the
 * office finds by accident.
 */
export const WEALTH_SUBJECT_STATUSES = ['prospect', 'client', 'contact'] as const;

/** All three, live from the first day: this vertical hands out everything it has. */
export const WEALTH_SUBJECT_STATUSES_LIVE = WEALTH_SUBJECT_STATUSES;

/**
 * **The security vertical's vocabulary** (security fold, 2026-09-22) —
 * protective-intelligence firms, whose people are not a pipeline but the cast of
 * a protection account.
 *
 *  - `principal` — the person the account exists to protect. The firm's own
 *    client, and this vertical's membership word.
 *  - `subject_of_interest` — somebody the firm is looking at because of the
 *    principal: a person who turned up at the gate, wrote to the office, or
 *    keeps appearing where the principal is.
 *  - `associate` — somebody on the account's record who is neither: family, a
 *    household employee, a business partner, a name that came up once.
 *
 * All three words used to be an array of *roles* on a membership row
 * (`security_account_subject.roles[]`). One status per subject is the same
 * statement every other vertical makes and the one the rest of the machinery can
 * act on: kinds and depth derive from it, the People list filters on it, and a
 * fold keeps the newer of two. A person who is both a principal in one account
 * and an associate in another is two records, because an account is a wall
 * (audit note §3 addendum) and the same real person in two accounts is already
 * two entities.
 *
 * None of these tokens is another vertical's, which is ordinary rather than
 * deliberate — the meaning is the vertical's either way.
 */
export const SECURITY_SUBJECT_STATUSES = ['principal', 'subject_of_interest', 'associate'] as const;

/** All three, live from the first day: nothing is held back. */
export const SECURITY_SUBJECT_STATUSES_LIVE = SECURITY_SUBJECT_STATUSES;

/**
 * Every registered vertical's vocabulary in one array, **with the repeats still
 * in it**. A fourth vertical appends to the spread, and nothing else moves.
 *
 * Not exported: what every reader wants is the deduped list below. This one
 * exists to carry the *type* — the union of every literal any vertical holds —
 * across the `Set` that a runtime dedupe would otherwise widen to `string`.
 */
const SUBJECT_STATUSES_WITH_REPEATS = [
  ...CLUB_SUBJECT_STATUSES,
  ...BUSINESS_SUBJECT_STATUSES,
  ...PERSONAL_SUBJECT_STATUSES,
  ...WEALTH_SUBJECT_STATUSES,
  ...SECURITY_SUBJECT_STATUSES,
] as const;

/** One entry per distinct word — the union's members, named once each. */
type SubjectStatusToken = (typeof SUBJECT_STATUSES_WITH_REPEATS)[number];

/**
 * **Every distinct status word any registered vertical holds**, in the order the
 * verticals are spread and each word appearing once.
 *
 * The dedupe arrived with the personal vertical (2026-09-09), which spells its
 * one word `contact` — the club's word for a different relationship. Two
 * verticals sharing a token is ordinary and will happen again: the meaning is
 * per-vertical, and this array is a list of *tokens*, not of relationships. It
 * happened again on 2026-09-16, when the wealth vertical arrived spelling two of
 * its three words `prospect` and `contact` and adding exactly one token,
 * `client`.
 *
 * It matters because of the two readers. `z.enum` below takes each member as an
 * alternative, and a repeated one is a duplicate alternative in every error
 * message the enum produces; and `mcp-instructions.spec.ts` walks this list
 * asking whether each word has a meaning written for it, which would ask twice
 * about a word two verticals share. Deduping here is what keeps both honest
 * without either having to know that a token can repeat.
 *
 * `z.enum` wants a **non-empty** tuple, and a `Set` cannot promise one, so the
 * list is split into a head and a rest. The element type is not asserted — it is
 * the union the `as const` above computed, carried through the `Set` by its type
 * argument — and the one assertion left covers only the head's existence, which
 * a spread of four non-empty vocabularies already guarantees and which no
 * expression of it in the type system can say.
 */
const [FIRST_SUBJECT_STATUS, ...FURTHER_SUBJECT_STATUSES] = [
  ...new Set<SubjectStatusToken>(SUBJECT_STATUSES_WITH_REPEATS),
];

export const ALL_SUBJECT_STATUSES: readonly [SubjectStatusToken, ...SubjectStatusToken[]] = [
  FIRST_SUBJECT_STATUS as SubjectStatusToken,
  ...FURTHER_SUBJECT_STATUSES,
];

/**
 * The union of every vertical's vocabulary — deliberately looser than a
 * discriminated union on `vertical` (verticals design note §0.7). The pairing of
 * `vertical` and `status` is the service's to enforce; the client keys its labels
 * by `vertical`. Refine to a discriminated union if the dashboard ever needs the
 * type safety more than it needs the simple shape.
 */
export const SubjectStatus = z.enum(ALL_SUBJECT_STATUSES);
export type SubjectStatus = z.infer<typeof SubjectStatus>;

/**
 * What a client needs to render a status control without naming a vertical:
 * keyed by `org.vertical`, exactly as the wire spells it.
 *
 * An unregistered key reads as `undefined`, and the caller renders nothing
 * (§0.3). It does **not** fall back to club: a build that does not carry the
 * org's vertical would otherwise offer the wrong words for the wrong
 * relationship.
 */
export const SUBJECT_VOCABULARY_BY_VERTICAL: Readonly<
  Record<
    string,
    { readonly statuses: readonly SubjectStatus[]; readonly live: readonly SubjectStatus[] }
  >
> = Object.freeze({
  club: Object.freeze({
    statuses: CLUB_SUBJECT_STATUSES,
    live: CLUB_SUBJECT_STATUSES_LIVE,
  }),
  business: Object.freeze({
    statuses: BUSINESS_SUBJECT_STATUSES,
    live: BUSINESS_SUBJECT_STATUSES_LIVE,
  }),
  personal: Object.freeze({
    statuses: PERSONAL_SUBJECT_STATUSES,
    live: PERSONAL_SUBJECT_STATUSES_LIVE,
  }),
  wealth: Object.freeze({
    statuses: WEALTH_SUBJECT_STATUSES,
    live: WEALTH_SUBJECT_STATUSES_LIVE,
  }),
  security: Object.freeze({
    statuses: SECURITY_SUBJECT_STATUSES,
    live: SECURITY_SUBJECT_STATUSES_LIVE,
  }),
});

/**
 * **The five status tones** (blend spec, "Status tones", 2026-09-30). Every
 * vertical's status words map onto these, and a status marker takes its color
 * from the tone, never from the word. The word is always printed beside the
 * marker, so two words sharing a tone stay distinct.
 *
 *  - `in` — part of the circle: member, partner, client, principal.
 *  - `candidate` — being considered: prospect, intro.
 *  - `known` — on record with no standing: contact, friend, associate.
 *  - `watched` — kept an eye on: subject of interest.
 *  - `closed` — no longer pursued: declined.
 *
 * The colors are the dashboard's (`--tone-<name>` in its token sheet); this file
 * only says which tone each word takes.
 */
export const STATUS_TONES = ['in', 'candidate', 'known', 'watched', 'closed'] as const;
export type StatusTone = (typeof STATUS_TONES)[number];

/**
 * Each vertical's words onto a tone, keyed by `org.vertical` exactly as
 * {@link SUBJECT_VOCABULARY_BY_VERTICAL} is. Each row is typed against its own
 * vocabulary, so a word added to a vertical without a tone here fails to compile.
 */
export const STATUS_TONE_BY_VERTICAL: Readonly<
  Record<string, Readonly<Partial<Record<SubjectStatus, StatusTone>>>>
> = Object.freeze({
  club: Object.freeze({
    member: 'in',
    prospect: 'candidate',
    contact: 'known',
    declined: 'closed',
  } satisfies Record<(typeof CLUB_SUBJECT_STATUSES)[number], StatusTone>),
  business: Object.freeze({
    partner: 'in',
    intro: 'candidate',
    friend: 'known',
  } satisfies Record<(typeof BUSINESS_SUBJECT_STATUSES)[number], StatusTone>),
  personal: Object.freeze({
    contact: 'known',
  } satisfies Record<(typeof PERSONAL_SUBJECT_STATUSES)[number], StatusTone>),
  wealth: Object.freeze({
    client: 'in',
    prospect: 'candidate',
    contact: 'known',
  } satisfies Record<(typeof WEALTH_SUBJECT_STATUSES)[number], StatusTone>),
  security: Object.freeze({
    principal: 'in',
    associate: 'known',
    subject_of_interest: 'watched',
  } satisfies Record<(typeof SECURITY_SUBJECT_STATUSES)[number], StatusTone>),
});

/**
 * The tone one status word takes in one vertical. A word or vertical this build
 * has no entry for reads as `known`: a marker is a scanning aid beside the word,
 * never the only statement, so a neutral fallback misleads nobody.
 */
export const statusTone = (vertical: string, status: SubjectStatus): StatusTone =>
  STATUS_TONE_BY_VERTICAL[vertical]?.[status] ?? 'known';

/**
 * **Whether the office is ever asked to pick a status word** (personal-vertical
 * note §7).
 *
 * A vertical with one word has no choice to offer. The word still applies — every
 * subject has a status, and the personal vertical's is `contact` — but a radio
 * with one option, a menu with one item and a filter chip that filters nothing
 * are all the same defect: a control that looks like a decision and is not one.
 * The four places that ask for the word read this and render nothing instead: the
 * record head's status control, the People chip filter, the new-person status
 * radio and the track door.
 *
 * Most places that *display* a status do not ask this and must stay that way:
 * the People list's Status column and the graph legend still print "Contact" on
 * a one-word vertical, because saying what somebody is is not the same as asking
 * for it.
 *
 * The record head is the deliberate exception, and it is why that control is on
 * the list above rather than left showing the word read-only. It is the head of
 * *one* record, and a word every record in the org shares says nothing about the
 * one you have open — where the list column still separates a tracked row from a
 * known one, and the legend still says what the color means.
 *
 * Counted on the **full** vocabulary rather than the live subset, which is the
 * conservative reading of the two: `live` is a subset of `statuses`, so a
 * one-word vocabulary is a one-live-word vocabulary and this answers `false` for
 * exactly the case the note describes. Counting live words instead would have
 * hidden the *filter* chips on a vertical that merely stopped handing a word out
 * — and those are the people a reader most wants to list.
 */
export const hasStatusChoice = (vertical: string): boolean =>
  (SUBJECT_VOCABULARY_BY_VERTICAL[vertical]?.statuses.length ?? 0) > 1;

/**
 * **Whether a vertical has anything that reads the org profile** (decided
 * 2026-09-03, Soroush).
 *
 * One prose field per vertical, and the field is offered only where that
 * vertical consumes it. `org_profile` has two readers — the `relationship_read`
 * analysis, which the business vertical derives, and `wealth_fit`, which the
 * wealth vertical derives for a prospect — and each takes the same paragraph in
 * its user turn under a header of its own. A club derives `club_fit`, which
 * reads `club_values` instead; nothing a club run produces has ever looked at
 * the profile. Asking a club to write one is asking for prose no run will read.
 *
 * The **setting itself stays core** and is unchanged: every org has an
 * `org_profile` on `OrgSettingsView`, run creation still snapshots it into the
 * run config when it is set, and the admin plane can still hold one. This map
 * is about the *Settings surface* — which vertical is asked for the field —
 * and about nothing else.
 *
 * Keyed by `org.vertical` exactly as the wire spells it, the same shape as
 * `SUBJECT_VOCABULARY_BY_VERTICAL` above. An unregistered key reads as
 * `undefined`, and the caller shows nothing (§0.3): a build serving a vertical
 * it does not carry cannot know whether that vertical reads a profile, and
 * absence is a no-op rather than a fabricated default. A vertical that grows a
 * reader flips its row here and the field appears.
 */
export const ORG_PROFILE_READ_BY_VERTICAL: Readonly<Record<string, boolean>> = Object.freeze({
  club: false,
  business: true,
  /* The personal vertical derives `person_summary` and nothing else: there is no
     org-wide statement to read a person against, and the ask travels in the note.
     Its Settings page shows Sensitivity and no prose field. */
  personal: false,
  /* The wealth vertical's prospect read (`wealth_fit`) reads each person against
     the office's own paragraph — who the office is and who it serves — so this
     vertical is asked for the profile, under its own label and its own help text
     (`ORG_PROFILE_FIELD_COPY.wealth`). It is the second reader of `org_profile`
     and the reason the agent's section table has two rows on that one key: one
     paragraph, two readings, a different header each. */
  wealth: true,
  /* A protective-intelligence firm's read is held against the **account's**
     engagement context — who this principal is and what the firm is protecting
     them from — which travels on the run config (`security_account`), not
     against one paragraph for the whole firm. A firm serving eight accounts has
     eight of those statements and no org-wide one, so asking for a profile here
     would be asking for prose no run reads. */
  security: false,
});

/** Whether this build should ask that vertical for an org profile at all. */
export const orgProfileHasReader = (vertical: string): boolean =>
  ORG_PROFILE_READ_BY_VERTICAL[vertical] === true;

/* ————————————————————————— record fields ————————————————————————— */

/**
 * **`subject_record.record_fields` is an open bag, and this is the one key
 * anything names** (2026-09-06, with the import module).
 *
 * There is no vocabulary of record-field keys and there deliberately is not one:
 * the intake extractor proposes whatever operational key a note carries,
 * `IntakeService.routeOperational` writes each the same way — `{value,
 * recorded_by_user_id, recorded_at, source, prose_input_id}` — and the record
 * screen humanizes the key to a label. A closed list would have meant a migration
 * every time a club wrote down something new about a person.
 *
 * `former_member` is named here because it is the one key a **rule** turns on
 * rather than a note happening to mention: a resigned member is a `contact` with
 * this field and the date they resigned (import note §1.15, the retention
 * principle — no fifth status word for a relationship that ended). The import
 * plan's `record_field` target writes it, and a reader asking "was this contact
 * once a member" has one spelling to ask about instead of guessing between
 * `former_member`, `resigned` and `ex_member`.
 */
export const FORMER_MEMBER_RECORD_FIELD = 'former_member';

/* ————————————————————————— analyses ————————————————————————— */

/**
 * Analysis kinds are config strings, not a closed enum (server-design.md §7.9a):
 * a vertical module derives its set from subject status, and facet summaries are
 * `facet_summary:<facet>`.
 */
export const ANALYSIS_STATUSES = ['current', 'stale', 'superseded'] as const;
export const AnalysisStatus = z.enum(ANALYSIS_STATUSES);
export type AnalysisStatus = z.infer<typeof AnalysisStatus>;

/**
 * **The club vertical's derivation for a prospect** — two of the three kinds are
 * produced by no other vertical.
 *
 * Named `ANALYSIS_KINDS_STAGE_1` until 2026-09-02, which read as core: one
 * vertical's kind set under a name that claimed to be *the* stage's, on a
 * constant sitting in the shared vocabulary every org's client reads. The
 * business vertical derives a different set from a different status, and a third
 * will derive another; there is no such thing as "the stage-1 kinds".
 *
 * Club vocabulary in `shared/src/vocab.ts` is allowed and says whose it is —
 * `CLUB_SUBJECT_STATUSES` beside it is the same arrangement. Its one reader is
 * `modules/club/club.service.ts`, which is what the server's club-leak gate
 * enforces.
 */
export const CLUB_PROSPECT_ANALYSIS_KINDS = [
  'person_summary',
  'club_fit',
  'conversation_notes',
] as const;

/**
 * **Readings whose absence does not make a run undelivered** (2026-10-09 outage):
 * the estimated net-worth band is a supplement to the dossier. The pipeline
 * leaves one out rather than failing the run (`SUPPLEMENTARY_KINDS` in
 * `agent/app/research/agents/summary.py`, restated), and delivery does not count
 * its absence against the run.
 */
export const SUPPLEMENTARY_ANALYSIS_KINDS: readonly string[] = ['wealth_band'];

export const AnalysisKind = z.string();
export type AnalysisKind = z.infer<typeof AnalysisKind>;

export const facetSummaryKind = (facet: string): string => `facet_summary:${facet}`;

/* ————————————————————————— facets ————————————————————————— */

/**
 * The six stage-1 facet keys. The authoritative registry is server-side
 * (`common/catalog/`); this is the stage-1 mirror the dashboard's refresh picker
 * reads, pending a registry read endpoint (ui-design.md §8 item 13).
 */
export const FACET_KEYS = [
  'background',
  'finances',
  'legal',
  'reputation',
  'network',
  'affiliations',
] as const;
export const FacetKey = z.enum(FACET_KEYS);
export type FacetKey = z.infer<typeof FacetKey>;

/**
 * A facet key on the wire is a registry key, not a closed enum: `config.initial[]`
 * validates strictly server-side against the live registry, and facet-complete
 * signals with unknown keys are accepted (server-design.md §5.2). Client-side we
 * type it as a string so a registry that grows does not break the dashboard.
 */
export const FacetKeyLoose = z.string().min(1);

export const FACET_LABELS: Record<FacetKey, string> = {
  background: 'Background',
  finances: 'Finances',
  legal: 'Legal',
  reputation: 'Reputation',
  network: 'Network',
  affiliations: 'Affiliations & interests',
};

/* ————————————————————————— fact types & brief sections ————————————————————————— */

/** The twenty-two catalog types (fact-catalog.md). */
export const FACT_TYPES = [
  'vitals',
  'education',
  'professional_license',
  'employment',
  'board_seat',
  'association_membership',
  'family_relation',
  'residence',
  'nationality',
  'financial_event',
  'investment',
  'digital_asset_holding',
  'litigation',
  'records_check',
  'broker_exposure',
  'onchain_exposure',
  'account_exposure',
  'interest',
  'event_participation',
  'achievement',
  'philanthropy',
  'social_tie',
  'org_relation',
  'org_profile',
  'event_profile',
  'finding',
] as const;
export const FactType = z.enum(FACT_TYPES);
export type FactType = z.infer<typeof FactType>;

/**
 * **The types no model is ever offered** — a deterministic pipeline leg writes
 * them or nothing does (catalog v9, 2026-09-22).
 *
 * `broker_exposure` is the first. Its fields are counts a leg took off one
 * provider record and a match basis a leg computed by comparing anchors; every
 * one of them is a measurement rather than a reading, and a model handed the
 * shape could only guess at them. So the intake extractor's schema block leaves
 * it out (`claim-schemas.ts`), which is enforcement rather than a sentence in a
 * prompt asking the model not to.
 *
 * It is a list about **who may write**, and deliberately not about who may
 * read: the type renders, files under a brief section and reaches the summary
 * substrate exactly as every other type does.
 */
export const DETERMINISTIC_ONLY_FACT_TYPES = [
  'broker_exposure',
  /* What a stranger can attribute to a principal on-chain (catalog v14): counts,
     a value band and years the on-chain exposure leg measured. */
  'onchain_exposure',
  /* Which accounts a principal's own identifiers open (catalog v15): counts and
     categories the account check measured. */
  'account_exposure',
] as const satisfies readonly FactType[];

/**
 * **The types research writes and the intake reader is never offered**
 * (catalog v13, 2026-10-01). `digital_asset_holding` is a snapshot read off a
 * captured on-chain record; a first-party note saying someone "holds a lot of
 * crypto" has no wallets behind it, and offering the shape would invite one.
 * Enforced the way {@link DETERMINISTIC_ONLY_FACT_TYPES} is: the intake schema
 * block leaves these out (`claim-schemas.ts`).
 */
export const RESEARCH_ONLY_FACT_TYPES = [
  'digital_asset_holding',
] as const satisfies readonly FactType[];

/** Whether the intake reader may be offered `type`. */
export const isOfferedToIntake = (type: string): boolean =>
  !isDeterministicOnlyFactType(type) &&
  !(RESEARCH_ONLY_FACT_TYPES as readonly string[]).includes(type);

/** Whether a model may be offered `type` at all — see {@link DETERMINISTIC_ONLY_FACT_TYPES}. */
export const isDeterministicOnlyFactType = (type: string): boolean =>
  (DETERMINISTIC_ONLY_FACT_TYPES as readonly string[]).includes(type);

/**
 * **Who may write each of those types**, as the prefix its leg stamps on
 * `produced_by.agent` (the exposure leg stamps `exposure_leg-v1`).
 *
 * The list above keeps the shape out of the model's hands; this is the same rule
 * at the **persistence boundary**, which is where it has to be enforced rather
 * than arranged. Leaving a shape out of a prompt makes it unlikely that a model
 * writes one; only the ingest makes it impossible. A `broker_exposure` is counts
 * somebody measured off one provider record on one date, so a claim carrying
 * that type and any other producer is a claim asserting a measurement nobody
 * took — and it lands on a `current_per_scope` lineage, where it would supersede
 * a real reading.
 *
 * **A prefix, because the version is part of the producer's name.** The agent
 * name carries its version by design (`exposure_leg-v1`), so pinning the whole
 * string would refuse the leg's own claims the day it becomes `-v2` — and
 * refusing a leg for having been revised is not the rule anybody wants.
 */
export const DETERMINISTIC_ONLY_FACT_PRODUCERS: Readonly<Record<string, string>> = Object.freeze({
  broker_exposure: 'exposure_leg-v',
  onchain_exposure: 'onchain_exposure_leg-v',
  account_exposure: 'osint_exposure_leg-v',
});

/**
 * Whether `agent` is the producer `type` reserves itself for. `true` for every
 * type nobody reserves, so a caller can ask this of any claim.
 */
export const isDeterministicOnlyProducer = (type: string, agent: unknown): boolean => {
  const prefix = DETERMINISTIC_ONLY_FACT_PRODUCERS[type];
  if (prefix === undefined) return true;
  return typeof agent === 'string' && agent.startsWith(prefix);
};

/**
 * **The types an import's note-reading still puts in front of somebody**
 * (2026-09-07 ruling).
 *
 * The office approving a file is a bulk approval of the backfill it carries, and
 * that now reaches what a model read *out of the notes in the file* as well as
 * what the columns said: several hundred `family_relation` and `employment` rows
 * landing in Needs Action on the day a club's history is imported is a queue
 * nobody works, and a queue nobody works is worse than no queue.
 *
 * These three are the exception, and they are the exception because of what a
 * wrong one costs. A misread suit, a misread transaction or a misread background
 * check is the material the gate was built for; a misread cousin is a line on
 * the network tab that the office corrects in one gesture. So an import's
 * reading of prose skips the gate for every other type and is gated normally for
 * these — which, since the reading's claims are first-party, means `queued`
 * rather than `blocking`: the fact lands and the question is batched.
 *
 * It says nothing about the *structural* pass. `bulkApproved` — the commit
 * writing the plan's own columns — approves everything, including these, because
 * the preview counted every one of those facts in front of a named person.
 */
export const IMPORT_REVIEWED_FACT_TYPES = [
  'litigation',
  'financial_event',
  'records_check',
] as const satisfies readonly FactType[];

export const isImportReviewedFactType = (type: string): boolean =>
  (IMPORT_REVIEWED_FACT_TYPES as readonly string[]).includes(type);

/**
 * **The fields each fact type holds**, in the catalog's own order — the
 * vocabulary an import plan's `{type, field}` pair is drawn from.
 *
 * The catalog itself is server-side (`server/src/common/catalog/fact-types.ts`)
 * and stays there: it carries the zod schemas, the dedup keys and the
 * cardinalities, none of which a client has any business holding. What a client
 * needs is narrower and is exactly this — **which words are legal after a type
 * has been chosen** — because the Plan tab renders that choice as a select and
 * an import plan whose `field` is a word the catalog never heard of is a column
 * that maps to nothing at commit.
 *
 * A copy of a server truth, and therefore fenced: `fact-type-fields.spec.ts`
 * asserts this table equals the catalog's, key for key and order for order, so
 * a field added to a schema without being added here fails a unit test rather
 * than reaching a plan editor that quietly offers the wrong list.
 */
export const FACT_TYPE_FIELDS: Readonly<Record<FactType, readonly string[]>> = Object.freeze({
  vitals: ['born', 'birthplace'],
  education: ['institution', 'degree', 'year'],
  professional_license: [
    'kind',
    'authority',
    'jurisdiction',
    'credential',
    'identifier',
    'status',
    'issued',
    'expires',
    'as_of',
  ],
  employment: ['org', 'role', 'start', 'end', 'founder'],
  board_seat: ['org', 'role', 'since', 'until'],
  association_membership: ['org', 'since', 'until'],
  family_relation: ['a', 'b', 'relation'],
  residence: ['place', 'kind', 'holding', 'value', 'value_kind', 'value_date'],
  nationality: ['country_iso2', 'basis'],
  financial_event: ['principal', 'kind', 'org', 'counterparty', 'date', 'magnitude'],
  investment: ['org', 'round', 'amount', 'date', 'role', 'detail'],
  digital_asset_holding: [
    'as_of',
    'wallets_counted',
    'wallets_predicted',
    'chains',
    'visible_usd',
    'predicted_usd',
    'borrowed_usd',
    'history_from',
    'peak_usd',
    'peak_year',
    'basis',
  ],
  litigation: ['case_name', 'court', 'filed', 'subject_role', 'matter', 'case_status'],
  records_check: ['check', 'scope', 'result', 'outcome', 'as_of'],
  broker_exposure: [
    'broker',
    'product',
    'outcome',
    'match_basis',
    'addresses_count',
    'phones_count',
    'emails_count',
    'relatives_count',
    'employers_count',
    'addresses_from',
    'addresses_to',
    'as_of',
  ],
  onchain_exposure: [
    'outcome',
    'name_search_matches',
    'name_search_finds_them',
    'wallets_read',
    'wallets_supplied',
    'wallets_verified',
    'wallets_predicted',
    'value_band',
    'history_from',
    'counterparties_named',
    'exchanges_used',
    'as_of',
  ],
  account_exposure: [
    'outcome',
    'identifiers_checked',
    'identifiers_with_accounts',
    'accounts_found',
    'kinds_with_accounts',
    'categories',
    'as_of',
  ],
  interest: ['interest', 'detail'],
  event_participation: ['event', 'edition', 'role', 'detail'],
  achievement: ['what', 'context', 'date'],
  philanthropy: ['donor', 'recipient', 'cause', 'detail', 'since'],
  social_tie: ['a', 'b', 'context', 'since'],
  org_relation: ['org', 'counterparty', 'relation', 'detail'],
  org_profile: ['description', 'location', 'jurisdiction', 'founded', 'size', 'sector', 'website'],
  event_profile: [
    'description',
    'starts_on',
    'ends_on',
    'place',
    'venue',
    'format',
    'expected_size',
    'website',
  ],
  finding: ['prose'],
} as const);

/** Whether `field` is a word {@link FACT_TYPE_FIELDS} holds for `type`. */
export const isFactTypeField = (type: FactType, field: string): boolean =>
  FACT_TYPE_FIELDS[type].includes(field);

/**
 * **The fields of {@link FACT_TYPE_FIELDS} a fact cannot be written without** —
 * the subset whose catalog schema is not `.nullish()`.
 *
 * A second mirror of a server truth, here for the same reason the first one is:
 * the catalog's zod schemas stay server-side, and what a client needs is the
 * closed answer to one question — *which of these words must a writer fill*. The
 * plan contract asks it (`import.ts`): a plan that maps a column to
 * `employment.role` and no column at all to `employment.org` describes a fact
 * the catalog will refuse, and refusing the plan is the only place that can be
 * said once for every row rather than 176 times.
 *
 * Fenced exactly as `FACT_TYPE_FIELDS` is, and by the same spec: `requiredFactFields`
 * derives the answer off the schemas and `fact-type-fields.spec.ts` asserts this
 * table equals it, key for key and in the catalog's own order. A field that stops
 * being nullish without being added here fails a unit test.
 *
 * `vitals` is the one type with nothing required: a person with a birthplace and
 * no birth date is a vitals fact, and so is the other way round.
 */
export const FACT_TYPE_REQUIRED_FIELDS: Readonly<Record<FactType, readonly string[]>> =
  Object.freeze({
    vitals: [],
    education: ['institution'],
    professional_license: ['kind', 'authority'],
    employment: ['org'],
    board_seat: ['org'],
    association_membership: ['org'],
    family_relation: ['a', 'b', 'relation'],
    residence: ['place'],
    nationality: ['country_iso2'],
    financial_event: ['principal', 'kind'],
    investment: ['org'],
    /* The nullable figures are absent for `broker_exposure`'s reason: the key
       must be present, and this table lists what a writer must fill. */
    digital_asset_holding: [
      'as_of',
      'wallets_counted',
      'wallets_predicted',
      'visible_usd',
      'basis',
    ],
    litigation: ['case_name', 'court'],
    records_check: ['check', 'scope', 'result'],
    /* `addresses_from` and `addresses_to` are absent because they are
       `.nullable()` and not `.nullish()`: the key must be present and its value
       may be null, and this table answers the narrower question of which words
       a writer must fill. `requiredFactFields` reads optionality the same
       conservative way for every catalog field, and the fence between the two
       is `fact-type-fields.spec.ts`. */
    broker_exposure: [
      'broker',
      'product',
      'outcome',
      'match_basis',
      'addresses_count',
      'phones_count',
      'emails_count',
      'relatives_count',
      'employers_count',
      'as_of',
    ],
    /* The nullable figures are absent for `broker_exposure`'s reason. */
    onchain_exposure: [
      'outcome',
      'name_search_matches',
      'name_search_finds_them',
      'wallets_read',
      'wallets_supplied',
      'wallets_verified',
      'wallets_predicted',
      'as_of',
    ],
    account_exposure: [
      'outcome',
      'identifiers_checked',
      'identifiers_with_accounts',
      'accounts_found',
      'kinds_with_accounts',
      'categories',
      'as_of',
    ],
    interest: ['interest'],
    event_participation: ['event', 'edition'],
    achievement: ['what', 'date'],
    philanthropy: ['donor'],
    social_tie: ['a', 'b', 'context'],
    org_relation: ['org', 'counterparty', 'relation'],
    org_profile: ['description'],
    event_profile: ['description'],
    finding: ['prose'],
  } as const);

/** Whether `field` is one {@link FACT_TYPE_REQUIRED_FIELDS} holds for `type`. */
export const isRequiredFactTypeField = (type: FactType, field: string): boolean =>
  FACT_TYPE_REQUIRED_FIELDS[type].includes(field);

/**
 * **The types a column may be pointed at**, and the ones the office cannot
 * import (the owner's ruling of 2026-09-09 for the first two; `broker_exposure`
 * at catalog v9 and `nationality` at v10 for the reasons noted on each below).
 *
 * `org_relation` is excluded because **both of its sides are entities the
 * planner cannot name from a single column**: `org` and `counterparty` are two
 * organisations (or an organisation and an event), and an import column carries
 * one value per row against one *person*. Every other fact target hangs off the
 * row's subject and needs the column to supply one side; this one would need it
 * to supply both, and there is no shape in the plan that says which.
 *
 * `org_profile` is excluded for the mirror-image reason: it has **no** entity
 * side at all, and its subject is an *organisation*. A plan's row subject is a
 * person, so a column pointed at it would write what a company does onto the
 * person the row is about. What an organisation says about itself reaches the
 * record through its own doors — the statements door and the lookup — and a
 * spreadsheet column is neither.
 *
 * It stays in {@link FACT_TYPE_FIELDS}, which is fenced against the catalog
 * type for type (`fact-type-fields.spec.ts`) and answers a different question —
 * *what fields does this type have* — for readers that render a fact rather than
 * plan an import.
 *
 * The exclusion is stated once, here, and read in three places: the plan contract
 * refuses a target naming an excluded type (`import.ts`), the planner is never
 * shown one (`plan-vocabulary.ts`), and the Plan tab does not offer one.
 */
export const IMPORT_UNTARGETABLE_FACT_TYPES = [
  'org_relation',
  'org_profile',
  /* An event's own account of itself (catalog v11): its subject is an event,
     and a row of a file is a person. */
  'event_profile',
  /* **A reading, not a column** (catalog v9). Every field of a
     `broker_exposure` is something the exposure leg measured off one provider
     record on one date; a spreadsheet cell saying "9 addresses" would be the
     office asserting a count nobody read. It is in
     {@link DETERMINISTIC_ONLY_FACT_TYPES} for the same reason read from the
     model's side. */
  'broker_exposure',
  /* **A measurement, not a column** (catalog v14): the on-chain exposure leg's
     counts and band, for `broker_exposure`'s reason. */
  'onchain_exposure',
  /* The account check's counts (catalog v15), for the same reason. */
  'account_exposure',
  /* **A reading of captured wallets, not a column** (catalog v13): a cell saying
     "$2M in crypto" has no wallets behind it. */
  'digital_asset_holding',
  /* **A code, and a column of nationalities is prose** (catalog v10). The one
     required field is `country_iso2` — two letters, because it is what a reader
     joins on — and what an office spreadsheet's "Nationality" column actually
     holds is "Irish", "Ireland", "British/Irish". Every one of those is a fact
     the catalog refuses, and a refused claim costs the **whole row** (import
     note §1.8): a plan pointed here would lose each row's residence, its
     memberships and its notes along with the citizenship. Shut until there is a
     transform that turns a nationality's name into its code — which is a plan
     grammar change and somebody's decision, not something to infer row by row.
     The research and intake doors are unaffected: they write a code because
     their schema asks for one. */
  'nationality',
] as const satisfies readonly FactType[];

/** The eighteen types an import plan's `{type, field}` pair may name. */
export const IMPORT_TARGETABLE_FACT_TYPES: readonly FactType[] = FACT_TYPES.filter(
  (type): type is FactType => !(IMPORT_UNTARGETABLE_FACT_TYPES as readonly string[]).includes(type),
);

/** Whether an import plan may point a column at `type`. */
export const isImportTargetableFactType = (type: string): boolean =>
  (IMPORT_TARGETABLE_FACT_TYPES as readonly string[]).includes(type);

/**
 * **What `org_relation.relation` may say** — the catalog's own enum, mirrored
 * here for the same reason {@link FACT_TYPE_FIELDS} is: the schema stays
 * server-side, and what a client needs is the closed list of words it may meet
 * on a fact it is rendering.
 *
 * Three of the four stand between two organisations, and the fixed slots make
 * the relation read from the subject outwards: `part_of` — the subject is a
 * campus, chapter, division or subsidiary of the counterparty; `governed_by` — the
 * counterparty is a board, regents or trustees over the subject;
 * `affiliated_with` — a stated tie that is neither.
 *
 * **`hosted` is the fourth and the odd one** (2026-09-09): the counterparty is
 * an *event*, not an organisation, and the subject is the organisation that put
 * it on — "medi.ci hosted Medici Day 2026". It is the one relation whose subject
 * is the larger side, and the one the "are these the same?" card never offers,
 * because that card only ever relates two organisations
 * ({@link MERGE_CARD_RELATIONS} in `decisions.ts`).
 */
export const ORG_RELATIONS = [
  'part_of',
  'governed_by',
  'affiliated_with',
  'hosted',
  'sponsored',
] as const;
export const OrgRelation = z.enum(ORG_RELATIONS);
export type OrgRelation = z.infer<typeof OrgRelation>;

/**
 * **What `investment.role` may say** — the catalog's own enum, mirrored here for
 * {@link FACT_TYPE_FIELDS}'s reason: the schema stays server-side, and what a
 * client needs is the closed list of words it may meet on a fact it renders.
 *
 * Two words and no third. `lead` is the investor a source names as leading the
 * round; `participant` is everybody else it names in it. A round nobody is said
 * to have led is a round where the field is simply absent — the catalog makes it
 * nullable for exactly that, and inventing a `sole` or an `unknown` would have
 * the record assert a structure the source did not state.
 */
export const INVESTMENT_ROLES = ['lead', 'participant'] as const;
export const InvestmentRole = z.enum(INVESTMENT_ROLES);
export type InvestmentRole = z.infer<typeof InvestmentRole>;

/**
 * **What `professional_license.kind` may say** — the catalog's own enum,
 * mirrored here for {@link FACT_TYPE_FIELDS}'s reason: the schema stays
 * server-side, and what a client needs is the closed list of words it may meet
 * on a fact it renders.
 *
 * Seven words, six of them a register the office actually reads and the seventh
 * the honest floor. `bar_admission` is a state bar's roll; `medical_license` a
 * state medical board's; `board_certification` a specialty board's diplomate
 * list, which is not a licence to practise at all and is named separately for
 * exactly that reason; `patent_registration` the USPTO roster of agents and
 * attorneys; `npi` the national provider identifier, which is a registry entry
 * rather than a permission; `securities_registration` a CRD-keyed broker or
 * adviser registration. `other` is what a register nobody enumerated lands on,
 * and it is a word rather than a refusal because a licence the catalog cannot
 * name is still a licence the office wants on the record — `authority` says
 * which body issued it whatever the kind reads.
 *
 * **It is not a severity axis.** What a licence is says nothing about whether
 * it is in good standing; `status` says that, and the ladder reads that field
 * alone (`professionalLicenseSeverity`, in the catalog).
 */
export const PROFESSIONAL_LICENSE_KINDS = [
  'bar_admission',
  'medical_license',
  'board_certification',
  'patent_registration',
  'npi',
  'securities_registration',
  'other',
] as const;
export const ProfessionalLicenseKind = z.enum(PROFESSIONAL_LICENSE_KINDS);
export type ProfessionalLicenseKind = z.infer<typeof ProfessionalLicenseKind>;

/**
 * **What `nationality.basis` may say** — the catalog's own enum, mirrored here
 * for {@link FACT_TYPE_FIELDS}'s reason: the schema stays server-side, and what
 * a client needs is the closed list of words it may meet on a fact it renders.
 *
 * Three words, and they name **what kind of statement the source made**, never
 * how likely it is to be true. `passport` — the source describes a passport the
 * person holds; `official_record` — a register, filing or government document
 * states the citizenship; `stated` — somebody said so in prose, the subject
 * included. A source that gives no basis writes none, which is the ordinary
 * case and is why the field is nullable.
 *
 * **It is not a confidence scale**, and the dashboard must not render it as
 * one: how well founded a fact is on this record is its source tier and its
 * corroboration count, and a second ladder sitting inside `fields` would
 * compete with the two the office already reads.
 */
export const NATIONALITY_BASES = ['passport', 'official_record', 'stated'] as const;
export const NationalityBasis = z.enum(NATIONALITY_BASES);
export type NationalityBasis = z.infer<typeof NationalityBasis>;

/**
 * The brief's seven section keys. They read the same as the facet keys by
 * coincidence of subject matter — sections are driven by the **fact type**, never
 * by the facet that produced the fact (facts carry no facet; ui-design.md §4.7).
 */
export const BRIEF_SECTIONS = [
  'background',
  'finances',
  'investors',
  'legal',
  'reputation',
  'network',
  'affiliations',
] as const;
export const BriefSection = z.enum(BRIEF_SECTIONS);
export type BriefSection = z.infer<typeof BriefSection>;

export const BRIEF_SECTION_LABELS: Record<BriefSection, string> = {
  background: 'Background',
  finances: 'Finances',
  investors: 'Investors',
  legal: 'Legal',
  reputation: 'Reputation',
  network: 'Network',
  affiliations: 'Affiliations & interests',
};

/** Section render order on the brief. */
export const BRIEF_SECTION_ORDER: readonly BriefSection[] = BRIEF_SECTIONS;

/**
 * Proposed — to be blessed into fact-catalog.md.
 *
 * design.md asserts "brief rendering rules (type → sections)" exist; nothing
 * writes them down and fact-catalog.md has no section column (ui-design.md §8
 * item 21). This is the proposal, mapping all twenty-two types onto the demo's
 * seven section labels.
 *
 * Judgment calls worth naming when this is blessed:
 *  - `philanthropy` → finances (giving is read as a financial act; its
 *    affiliation flavour is already carried by `board_seat` on the foundation);
 *  - `achievement` → reputation (public accomplishment, the section where the
 *    demo's "modest public profile" reading lives);
 *  - `board_seat` → network (co-board derivations are the network's backbone),
 *    while `association_membership` → affiliations (club/association belonging);
 *  - `finding` → reputation as the default home for keyless prose findings.
 */
export const FACT_TYPE_BRIEF_SECTION: Record<FactType, BriefSection> = {
  vitals: 'background',
  education: 'background',
  /* **Beside the degree** (catalog v8, 2026-09-14). A credential is the same
     sort of statement `education` is — what the person is qualified to do, in
     the plain account of them that comes before anything is read off it — and
     the office reads "admitted to the Pennsylvania bar in 2004" next to where
     they went to law school rather than in a section of its own. Discipline is
     a different question and has a different home: a suspension or a
     disbarment arrives as `records_check(regulatory)` in Legal, and this
     section carries the licence itself. */
  professional_license: 'background',
  employment: 'background',
  residence: 'background',
  /* **Background, beside where they live** (catalog v10). A citizenship is the
     same sort of plain statement about a person `vitals` and `residence` are,
     and it is read in the same breath as them — which country's consulate is
     this traveler's is a question about who they are, not about what anybody
     found out. */
  nationality: 'background',
  financial_event: 'finances',
  philanthropy: 'finances',
  /* **Its own section, and not a corner of Finances** (owner ruling, 2026-09-10:
     "A separate section from leadership"). Who put money into a business is a
     different question from what the business did with money — the office reads
     the first as a list of people and funds it may already know, and the second
     as events. Sharing a section would have sorted a name in among the rounds it
     belongs to and made the list unreadable as a list. */
  investment: 'investors',
  /* **Finances** (catalog v13): what a subject's known wallets hold is read
     beside what they bought and sold, and it is a snapshot of these wallets,
     never a net worth. */
  digital_asset_holding: 'finances',
  litigation: 'legal',
  records_check: 'legal',
  /* **Background, not Legal** (catalog v9). A broker listing is not a
     proceeding and not an allegation: it is a plain statement about what is
     publicly purchasable about this person, which reads beside where they live
     and what they do rather than among the suits and the checks. */
  broker_exposure: 'background',
  /* Beside the broker listing, for its reason: what a stranger can attribute to
     this person on-chain is a statement about what is public, not a finance. */
  onchain_exposure: 'background',
  /* Beside the other two exposure readings: what a principal's own identifiers
     open is a statement about what is public. */
  account_exposure: 'background',
  achievement: 'reputation',
  finding: 'reputation',
  family_relation: 'network',
  social_tie: 'network',
  board_seat: 'network',
  /* One organisation standing inside or under another is a shape of the
     network, not a fact about a person's background — and the brief reads it
     the way it reads a board seat: as something the record's map is made of. */
  org_relation: 'network',
  /* What an organisation does, where it sits and how big it is is the same sort
     of statement `employment` and `education` are about a person: the plain
     account of the thing, before anything is read off it. */
  org_profile: 'background',
  event_profile: 'background',
  association_membership: 'affiliations',
  event_participation: 'affiliations',
  interest: 'affiliations',
};

export const briefSectionFor = (factType: FactType): BriefSection =>
  FACT_TYPE_BRIEF_SECTION[factType];

/* ————————————————————————— metering stages ————————————————————————— */

/**
 * The pipeline legs a metered model call can belong to — the `stage` on a
 * per-call `ai_usage_event` row (server-design.md §9).
 *
 * **Derived from the call sites, not invented.** Every entry names an agent the
 * research pipeline actually runs, in the order a run meets them: the two
 * quick-search agents (`agent/app/research/graphs/quick_search.py`), the
 * quick-search summary (`agents/summary.build_simple_summary`), the two halves of
 * a facet pair (`agents/facets.build_retriever` / `build_extractor`, and the
 * extractor's one retry), the round-planning verdict (`agents/orchestrator`) and
 * the summary stage's per-kind analysis legs
 * (`agents/summary.build_analysis_leg`). A leg the pipeline does not run has no
 * business being in a vocabulary the server validates against.
 *
 * **Server-side calls are deliberately absent.** Intake extraction and fuzzy
 * resolution / dedup adjudication meter through `recordServerUsage`, whose
 * idempotency key is `(source, source_ref_id, model_id)` — a stage dimension
 * would change what "the same call" means there. Those rows carry `source`
 * instead, and read as "not broken down" in the admin breakdown.
 *
 * The agent mirrors this tuple as `USAGE_STAGES` in `agent/app/core/metering.py`
 * and a test compares the two (`agent/tests/core/test_metering.py`).
 */
export const USAGE_STAGES = [
  'quick_search_harvest',
  'quick_search_merge',
  'simple_summary',
  'facet_retrieve',
  'facet_extract',
  'orchestrator',
  'analysis',
  // Reports (docs/notes/2026-09-22-reports-design.md): reading the prose the
  // office wrote, collecting public material (a collecting type only), and
  // writing a version. No apostrophes in this list: the agent pin test reads it
  // with a quote-matching pattern.
  'report_inputs',
  'report_collect',
  'report_write',
] as const;
export const UsageStage = z.enum(USAGE_STAGES);
export type UsageStage = z.infer<typeof UsageStage>;

/* ————————————————————————— the membership word ————————————————————————— */

/**
 * **Which of a vertical's words means "one of us"** — a full member of the club,
 * a firm's `partner` (import note §1.14).
 *
 * One reader today: the import planner, which tells the model which status a
 * person who turns out to be a **user of this system** should be proposed at, and
 * which the plan's `user_match: 'propose_member'` resolves to when preview finds
 * that person. A club importing its own contact board has Lew somewhere in it,
 * and "the person running the import is a member" is the one thing about the file
 * nobody should have to say.
 *
 * Keyed by `org.vertical` exactly as the wire spells it, the same shape and the
 * same rule as {@link SUBJECT_VOCABULARY_BY_VERTICAL}: an unregistered key comes
 * back from {@link memberEquivalentStatus} as `null` and the caller says nothing
 * rather than picking a word. A build
 * that does not carry the org's vertical does not know which of its words means
 * membership, and a fabricated default is worse than an absence — it would put
 * every user of the system on the wrong footing on the record.
 *
 * Here rather than on the server's `Vertical` because the office reads it too:
 * the People tab shows the proposed status beside "this is a user of this
 * system", and a page naming a word the server would not use is the drift this
 * package exists to prevent.
 */
export const MEMBER_EQUIVALENT_STATUS_BY_VERTICAL: Readonly<Record<string, SubjectStatus>> =
  Object.freeze({
    club: 'member',
    business: 'partner',
    /* One word, so the membership word is that word: a personal org's own user
       is filed the same way everyone else on its record is. */
    personal: 'contact',
    /* The established relationship, whichever seat is reading: a client family
       for a wealth manager, a manager or co-investor for a family office. The
       import's `propose_member` resolves here, and the import's *default* is
       `statuses[0]` — `prospect` — which is the right footing for the first file
       either seat uploads. */
    wealth: 'client',
    /* The person the account exists for. A user of this system who turns up in
       a security firm's import is on the protection side of the account, not
       somebody it is looking at — and `subject_of_interest` is the one word in
       this vocabulary it would be actively wrong to propose for them. */
    security: 'principal',
  });

/** The vertical's word for a full member, or `null` when this build has none. */
export const memberEquivalentStatus = (vertical: string): SubjectStatus | null =>
  MEMBER_EQUIVALENT_STATUS_BY_VERTICAL[vertical] ?? null;

/**
 * The accounts research reads for a person: X and Bluesky posts
 * (`social/handles.py`), crypto wallets (`onchain/wallets.py`), and a confirmed
 * on-chain identity.
 */
export const PROFILE_LINK_PLATFORMS = [
  'x',
  'bluesky',
  /* A crypto wallet on any chain: the chain is a label (`ProfileLink.chain`),
     never a platform of its own, so a chain the provider adds needs no change. */
  'wallet',
  /* A confirmed on-chain identity: the entity whose wallets research reads. */
  'onchain_entity',
] as const;
/** What an owner can do with one of a person's accounts (the `profile_link` decision). */
export const PROFILE_LINK_ACTIONS = ['confirm', 'set_aside', 'remove'] as const;
